/* eslint-disable */
/*
 * CAPTURE-OWNED MODULE
 * Review Detail page — two-column layout for reviewing a paused workflow execution.
 * Left: PDF document viewer. Right: editable extracted fields + approve/reject actions.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { captureAPI } from '../services/api';
import { getExtractedFields, getLineItems, updateLineItem, reclassifyDocument } from '../api/captureExtraction';
import {
  visibleLineItemColumns,
  isEditableLineItemColumn,
  isNumericLineItemColumn,
  coerceLineItemValue,
  lineItemInputSeed,
  hasManualLineItemCorrections,
} from '../utils/lineItemEditing';
import { buildTemplateFromExtraction } from '../api/captureTemplates';
import { useCanBuildWorkflows, useIsAdmin } from '../identity/useIsAdmin';
import PublishStatusNotice from '../components/PublishStatusNotice';

// The newest workflow run of a document, or null when it has none. Throws when
// the answer cannot be had: "no run" must never be inferred from a failed read.
async function findLatestRun(documentId) {
  const res = await captureAPI.workflows.listExecutionsForDocument(documentId);
  if (!res?.ok || !Array.isArray(res.executions)) {
    throw new Error('Could not check whether this document belongs to a workflow run. Try again.');
  }
  return res.executions[0] || null;
}

// The newest run, if it is one a reviewer still acts on (paused or failed).
async function findLiveRun(documentId) {
  const run = await findLatestRun(documentId);
  return run && (run.status === 'paused' || run.status === 'failed') ? run : null;
}
import { formatDocType } from '../utils/formatDocType';
import {
  selectableDocumentTypes,
  shouldWarnLostAccess,
  lostAccessMessage,
  confirmDisabled as typeConfirmDisabledCheck,
  correctionErrorMessage,
} from '../utils/typeCorrection';
import { shouldAutoOpen } from '../utils/classificationEvidence';
import { formatFieldValue } from '../utils/formatFieldValue';
import { fieldReason } from '../utils/fieldReason';
import { supabase } from '../auth/supabaseClient';
import SimplePDFViewer from '../components/SimplePDFViewer';
import FieldCorrectionModal from '../components/FieldCorrectionModal';
import { buildCorrectionBody } from '../utils/correctionPayload';
import DocumentStudio from '../components/DocumentStudio';
import ClassificationEvidencePanel from '../components/ClassificationEvidencePanel';
import SetupProposalPanel from '../components/SetupProposalPanel';
import VendorBlock from '../components/VendorBlock';
import { formatAiCostUsd } from '../utils/formatAiCost';
import './ReviewDetailPage.css';

/**
 * The run that replaced a cancelled one: Re-extract cancels the document's
 * paused runs and starts a new one (capture-app reextractTrigger). Null when
 * the document has no other live run, or it cannot be read.
 */
export async function findReplacingRun(documentId, cancelledRunId) {
  const live = await findLiveRun(documentId).catch(() => null);
  return live && live.id !== cancelledRunId ? live : null;
}

/** The Next / Previous list with one run's id swapped for the run that replaced it. */
export function replaceSiblingId(siblingIds, oldId, newId) {
  return (siblingIds || []).map((id) => (id === oldId ? newId : id));
}

export const RUN_REPLACED_TEXT = 'This document was re-extracted, so this run was replaced. Showing the current run.';

const CAPTURE_API_BASE = process.env.REACT_APP_CAPTURE_API_BASE_URL || '';

// Linked Documents is retiring; hidden until full removal.
// See vault decisions/capture/2026-07-19-document-relationships-scope-and-duplicate-detection.md
const SHOW_DOCUMENT_LINKS = false;

async function getAuthHeaders(contentType) {
  const headers = {};
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) headers['Authorization'] = `Bearer ${token}`;
  } catch { /* non-fatal */ }
  if (contentType) headers['Content-Type'] = contentType;
  return headers;
}

async function captureGet(path) {
  const headers = await getAuthHeaders();
  return axios.get(`${CAPTURE_API_BASE}${path}`, { headers });
}

async function capturePost(path, body, extraHeaders) {
  const headers = await getAuthHeaders('application/json');
  return axios.post(`${CAPTURE_API_BASE}${path}`, body, { headers: { ...headers, ...extraHeaders } });
}

async function capturePatch(path, body) {
  const headers = await getAuthHeaders('application/json');
  return axios.patch(`${CAPTURE_API_BASE}${path}`, body, { headers });
}

async function captureDelete(path) {
  const headers = await getAuthHeaders();
  return axios.delete(`${CAPTURE_API_BASE}${path}`, { headers });
}

async function capturePostForm(path, formData) {
  const headers = await getAuthHeaders();
  return axios.post(`${CAPTURE_API_BASE}${path}`, formData, { headers });
}

// ── Helpers ──

// Static fallback label map — used when field definitions API is unavailable
const FALLBACK_LABEL_MAP = {
  vendor_name: 'Vendor Name',
  purchase_order_number: 'PO Number',
  po_number: 'PO Number',
  invoice_number: 'Invoice Number',
  invoice_date: 'Invoice Date',
  due_date: 'Due Date',
  terms: 'Payment Terms',
  subtotal: 'Subtotal',
  tax: 'Tax',
  shipping: 'Shipping',
  total: 'Total',
  customer_name: 'Customer Name',
  customer_address: 'Customer Address',
  vendor_address: 'Vendor Address',
};

// Static fallback order — used when field definitions API is unavailable
const FALLBACK_ORDER = {
  vendor_name: 1, invoice_number: 2, invoice_date: 3, due_date: 4,
  po_number: 5, purchase_order_number: 5, terms: 6, subtotal: 7,
  tax: 8, shipping: 9, total: 10,
};

// Fields excluded from INDEX FIELDS section (rendered separately)
const EXCLUDED_FROM_INDEX = ['line_items'];

// Line-item columns that hold currency and get display-only currency formatting
// (via formatFieldValue). `total_price` is this schema's actual currency column
// (see NUMERIC_LINE_ITEM_COLUMNS in utils/lineItemEditing); the rest are the
// common cross-schema currency names. Quantity and every other column are left
// exactly as they render today — no commas, no symbol.
const CURRENCY_LINE_ITEM_COLUMNS = [
  'unit_price', 'total_price', 'amount', 'total', 'extended_price', 'line_total', 'price',
];
function isCurrencyLineItemColumn(key) {
  return CURRENCY_LINE_ITEM_COLUMNS.includes(key);
}

// `labels` and `order` come from the loaded field definitions (component
// state, keyed to the document); both default to none, which leaves the static
// fallbacks above.
function prettyLabel(key, labels = {}) {
  if (labels[key]) return labels[key];
  if (FALLBACK_LABEL_MAP[key]) return FALLBACK_LABEL_MAP[key];
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function sortFieldKeys(keys, order = {}) {
  return [...keys].sort((a, b) => {
    const oa = order[a] ?? FALLBACK_ORDER[a] ?? 500;
    const ob = order[b] ?? FALLBACK_ORDER[b] ?? 500;
    if (oa !== ob) return oa - ob;
    return a.localeCompare(b);
  });
}

/**
 * Labels and sort order from a field-definitions response
 * ({ key: { label, display_order, field_type, origin? } }). A definition with
 * no display_order sorts after the numbered ones, in response order.
 */
export function fieldDefLookups(fieldDefs) {
  const labels = {};
  const order = {};
  Object.entries(fieldDefs).forEach(([key, def], idx) => {
    if (def?.label) labels[key] = def.label;
    order[key] = def?.display_order != null ? def.display_order : 900 + idx;
  });
  return { labels, order };
}

/**
 * The Index Fields rows, in display order, and which of them are template-only.
 *
 * With definitions loaded, the rows are the definitions (the library's, plus
 * the applied template's own fields the server tagged origin 'template'). On a
 * document with an applied template, a key the TEMPLATE extracted that is no
 * longer in the definitions — the template was edited after this document ran
 * — is kept as a template-only row while it has a value, so nothing extracted
 * disappears from review. With no definitions, every extracted field is shown,
 * one per label (legacy behaviour).
 *
 * The saved order is ORDER-ONLY: saved keys still present come first in saved
 * order, then the rest in default order. A stale preference never hides a field.
 */
export function buildIndexFieldKeys({ fieldDefs, fields, sourceMap, appliedTemplateId, fieldOrderPref }) {
  const { labels, order } = fieldDefLookups(fieldDefs);
  const templateOnly = new Set();
  let available;
  if (Object.keys(fieldDefs).length > 0) {
    // Exclude line_items; it is rendered separately in the LINE ITEMS section
    const fieldDefKeys = Object.keys(fieldDefs).filter(key => !EXCLUDED_FROM_INDEX.includes(key));
    for (const key of fieldDefKeys) {
      if (fieldDefs[key]?.origin === 'template') templateOnly.add(key);
    }
    const kept = appliedTemplateId
      ? Object.keys(fields).filter((key) =>
        !(key in fieldDefs)
        && !EXCLUDED_FROM_INDEX.includes(key)
        && sourceMap[key] === 'template'
        && fields[key] != null && fields[key] !== '')
      : [];
    for (const key of kept) templateOnly.add(key);
    available = [...sortFieldKeys(fieldDefKeys, order), ...sortFieldKeys(kept)];
  } else {
    const labelSeen = {};
    const deduped = [];
    for (const key of sortFieldKeys(Object.keys(fields), order)) {
      const label = prettyLabel(key, labels);
      if (labelSeen[label] === undefined) {
        labelSeen[label] = key;
        deduped.push(key);
      } else {
        const prevKey = labelSeen[label];
        const prevHasValue = fields[prevKey] != null && fields[prevKey] !== '';
        const thisHasValue = fields[key] != null && fields[key] !== '';
        if (thisHasValue && !prevHasValue) {
          const idx = deduped.indexOf(prevKey);
          if (idx !== -1) deduped[idx] = key;
          labelSeen[label] = key;
        }
      }
    }
    available = deduped;
  }

  if (!fieldOrderPref) return { keys: available, templateOnly };
  const availableSet = new Set(available);
  const savedSet = new Set(fieldOrderPref);
  return {
    keys: [
      ...fieldOrderPref.filter((k) => availableSet.has(k)),
      ...available.filter((k) => !savedSet.has(k)),
    ],
    templateOnly,
  };
}

function relativeTime(isoString) {
  if (!isoString) return '';
  try {
    const diffMs = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  } catch {
    return '';
  }
}

function getDocIcon(mimeType) {
  if (mimeType?.includes('pdf')) return '\uD83D\uDCC4';
  if (mimeType?.includes('image')) return '\uD83D\uDDBC\uFE0F';
  return '\uD83D\uDCC1';
}

// Top-bar status pill (backlog #3a). Drives off the document's persisted
// status. 'published' is intentionally absent — Sub-section B's green
// "✅ Published" badge in the action bar is the canonical display for
// that case. Unknown statuses render no pill at all (better than a
// stale "Paused — Awaiting Review" string for documents in transient
// states the map doesn't cover yet).
const PILL_BY_STATUS = {
  pending_review:        { label: 'Paused — Awaiting Review', tone: 'warning' },
  needs_review:          { label: 'Paused — Awaiting Review', tone: 'warning' },
  processing_ocr:        { label: 'OCR in progress',          tone: 'info' },
  processing_extraction: { label: 'Extracting fields',        tone: 'info' },
  ocr_complete:          { label: 'OCR complete',             tone: 'info' },
  extracted:             { label: 'Extracted',                tone: 'info' },
  // A split parent is a batch container, not a document under review. Without
  // this it rendered no pill at all, which read as "still loading".
  split:                 { label: 'Split into parts',         tone: 'info' },
  // Slice 3c. Not normally seen here — the page redirects a held container to
  // the split review — but it must never render as nothing.
  pending_boundary_review: { label: 'Awaiting split review',  tone: 'warning' },
  failed:                { label: 'Extraction failed',        tone: 'error' },
  error:                 { label: 'Error',                    tone: 'error' },
};

/**
 * Did this run fail AT ITS PUBLISH NODE? Read from the run itself:
 * error_node_id names the node, and node_log (which the engine persists with
 * the failure) gives that node's type. Independent of the document's
 * publish_status on purpose: the notice once depended on that field alone, and
 * the document route was not sending it.
 */
export function runFailedAtPublish(execution, nodeTypeById = null) {
  if (!execution || execution.status !== 'failed') return false;
  const log = Array.isArray(execution.node_log) ? execution.node_log : [];
  const failedId = execution.error_node_id || execution.current_node_id;
  const entry = [...log].reverse().find((n) => n?.node_id === failedId && n?.status === 'failed')
    || [...log].reverse().find((n) => n?.status === 'failed');
  if (entry) return entry.node_type === 'output_publish';
  // A run the sweep INTERRUPTED has no failed log entry: the engine never got
  // to write one. The node it stopped at is error_node_id, and its type comes
  // from the workflow definition, when the page has it.
  return Boolean(nodeTypeById) && nodeTypeById[failedId] === 'output_publish';
}

/**
 * The step a FAILED run stopped at: its node id, and its type and label where
 * anything on the page knows them. Null for a run that has not failed or that
 * names no node. The node is error_node_id, else current_node_id: the same
 * rule the server's retry-failed-node uses. A run the sweep interrupted has no
 * failed log entry or step row, so every source is tried.
 */
export function failedStepOf(execution, steps = [], nodeTypeById = null) {
  if (!execution || execution.status !== 'failed') return null;
  const nodeId = execution.error_node_id || execution.current_node_id;
  if (!nodeId) return null;
  const log = Array.isArray(execution.node_log) ? execution.node_log : [];
  const entry = [...log].reverse().find((n) => n?.node_id === nodeId);
  const step = [...(Array.isArray(steps) ? steps : [])].reverse().find((s) => s?.node_id === nodeId);
  return {
    nodeId,
    type: entry?.node_type || step?.node_type || nodeTypeById?.[nodeId] || null,
    label: entry?.label || step?.node_label || null,
  };
}

/** What a retry answered, in words: the server's own reason when it gave one. */
export function retryRefusalText(errOrResult, fallback) {
  return errOrResult?.response?.data?.error || errOrResult?.error || fallback;
}

/**
 * The inline error for an Approve whose run then failed at a step that is not
 * Publish (code 'node_failed'). The server's line already names the step; the
 * label is added only when it does not. Then who can retry it.
 */
export function approveNodeFailedText(err, isAdmin) {
  const label = err?.failedNode?.label || null;
  const reason = (err?.message || 'The workflow stopped').replace(/\.$/, '');
  const named = label && !reason.includes(`"${label}"`)
    ? `the workflow stopped at "${label}": ${reason}`
    : reason.charAt(0).toLowerCase() + reason.slice(1);
  const id = err?.errorId ? ` (error_id: ${err.errorId})` : '';
  const next = isAdmin
    ? 'Use "Retry failed step" above.'
    : 'An administrator can retry it with "Retry failed step".';
  return `Approved, but ${named}${id}. ${next}`;
}

/**
 * The toast for a re-extract the route accepted. The route sends status,
 * restartedAt and profileOverrideApplied (no field count, no source), so the
 * count is the fields reloaded after the run, or null when that reload failed.
 * profileOverrideApplied only means the profile was sent: a matching template
 * still comes first and sets it aside.
 */
export function reextractSuccessText(result, fieldCount) {
  if (result?.restartedAt === 'action_split') {
    return 'Re-extraction started. This batch is being split again; its documents will appear on the Processing Monitor as they are made.';
  }
  const count = fieldCount == null ? 'Re-extraction finished' : `Re-extracted: ${fieldCount} field${fieldCount === 1 ? '' : 's'}`;
  const run = result?.status === 'paused' ? ' Waiting for review.' : result?.status === 'completed' ? ' Workflow complete.' : '';
  const profile = result?.profileOverrideApplied ? ' Your vendor profile was used only if no template matched.' : '';
  return `${count}.${run}${profile}`;
}

/**
 * One GET extracted-fields answer as the rail's flat key-maps. The first load
 * and a re-extract both read the rows through this, so a re-extract replaces
 * the status and evidence the per-field reason is built from along with the
 * values; it used to replace only the values and sources, and the reason kept
 * describing the previous run until the page was opened again.
 */
export function mapExtractedFields(rows) {
  const fields = {};
  const sourceMap = {};
  const statusMap = {};
  const evidenceMap = {};
  for (const f of rows || []) {
    const val = f.value_text ?? f.value_number ?? f.value_date ?? null;
    fields[f.field_key] = val != null ? String(val) : '';
    if (f.source != null) sourceMap[f.field_key] = f.source;
    if (f.status != null) statusMap[f.field_key] = f.status;
    if (f.evidence != null) evidenceMap[f.field_key] = f.evidence;
  }
  return { fields, sourceMap, statusMap, evidenceMap };
}

export { formatAiCostUsd };

const callsText = (n) => `${n} ${n === 1 ? 'call' : 'calls'}`;

/**
 * The document's vendor snapshot (slice 2b-3): the six vendor_* keys of GET
 * /documents/:id, a write's { vendor } or a correction's { vendor }. Missing
 * keys (an older capture-service) read as null.
 */
export function vendorSnapshotOf(src) {
  if (!src) return null;
  return {
    vendor_id: src.vendor_id ?? null,
    vendor_display_name: src.vendor_display_name ?? null,
    vendor_display_name_source: src.vendor_display_name_source ?? null,
    vendor_number: src.vendor_number ?? null,
    vendor_number_source: src.vendor_number_source ?? null,
    vendor_stamped_at: src.vendor_stamped_at ?? null,
  };
}

/** The document's printed vendor name for the Vendor block: the corrected value first. */
export function printedVendorName(correctedFields, fields) {
  const v = correctedFields?.vendor_name ?? fields?.vendor_name ?? null;
  if (v === null || v === undefined) return null;
  const text = String(v).trim();
  return text || null;
}

/**
 * The header's AI cost line, from GET /documents/:id `ai_cost`. "to date"
 * because the sum covers every run of the document: a re-extract never
 * removes capture_ai_calls rows. Null when there is nothing to show.
 */
export function aiCostText(aiCost) {
  if (!aiCost || !(aiCost.call_count > 0)) return null;
  return `AI cost ${formatAiCostUsd(aiCost.total_usd)} to date \u00b7 ${callsText(aiCost.call_count)}`;
}

/** The line's tooltip: one row per call type, most expensive first. */
export function aiCostTooltip(aiCost) {
  if (!aiCost || !(aiCost.call_count > 0)) return null;
  const rows = Object.entries(aiCost.by_type || {})
    .sort(([, a], [, b]) => (b.usd || 0) - (a.usd || 0))
    .map(([type, t]) => `${type.replace(/_/g, ' ')}: ${callsText(t.calls)}, ${formatAiCostUsd(t.usd)}`);
  return ['All AI calls for this document, including earlier runs and re-extracts.', ...rows].join('\n');
}

/**
 * The inline error for a re-extract that was refused or failed. The route's
 * reason is in `error`; `message` is set only when `error` is a code (a
 * document with no workflow), so it is read first.
 */
export function reextractFailureText(body) {
  return body?.message || body?.error || 'Re-extraction failed.';
}

/**
 * The header's run line: a word for the run's status and when it got there.
 * Paused counts from when the run was created; an ended run from when it ended.
 */
export function runMetaText(execution) {
  if (!execution) return null;
  const ended = execution.completed_at || execution.updated_at;
  const since = (word, when) => (when ? `${word} ${relativeTime(when)}` : word);
  switch (execution.status) {
    case 'paused': return execution.created_at ? since('Paused', execution.created_at) : null;
    case 'failed': return since('Failed', ended);
    case 'completed': return since('Completed', ended);
    case 'cancelled': return since('Cancelled', ended);
    case 'running': return 'Running';
    default: return null;
  }
}

/**
 * The top-bar pill. A run that has FAILED says so, whatever the document's
 * pipeline status is: a publish failure leaves the document at pending_review,
 * which on its own reads "Paused — Awaiting Review".
 */
export function statusPill({ documentStatus, execution, publishFailed }) {
  if (documentStatus === 'published') return null;
  if (execution?.status === 'failed') {
    return publishFailed
      ? { label: 'Publish failed', tone: 'error' }
      : { label: 'Workflow run failed', tone: 'error' };
  }
  if (publishFailed) return { label: 'Publish failed', tone: 'error' };
  return PILL_BY_STATUS[documentStatus] || null;
}

// ── Editable field row ──
//
// Exported for its own test: the page around it pulls in the PDF viewer, the
// canvas studio and the Supabase client, none of which a row's markup depends
// on. The page's default export is unchanged.

export function FieldRow({
  fieldKey, label: labelProp, templateField, value, fieldType, flagged, reason, isCorrected, isCorrecting, onCorrect,
  dragEnabled, isDragging, isDragOver,
  onGripDragStart, onRowDragOver, onRowDrop, onDragEnd,
}) {
  // Display-only: currency renders as $1,234.56; everything else unchanged.
  // The RAW `value` still backs the correction pencil / modal / POST.
  const displayValue = formatFieldValue(value, fieldType);
  const label = labelProp || prettyLabel(fieldKey);
  // Field values are read-only here. Edits go through the Field Correction
  // modal — opened either by the toolbar pencil (canvas-region flow) or by
  // this row's own pencil button (pre-scoped to fieldKey). The previous
  // click-into-field inline edit affordance was removed in Section 5: it
  // wrote to capture_extracted_fields without ever calling the corrections
  // endpoint, so corrections never reached vendor_profile_observations.
  //
  // Reorder: only the ⋮⋮ grip is draggable; the row itself is not, so the
  // correction pencil and row content stay fully interactive. The row is the
  // drop target.
  //
  // A CLEAN row — not flagged, no reason — must render exactly the markup it
  // rendered before reasons existed, down to the trailing space in the value's
  // class attribute (pinned by ReviewDetailPage.fieldRow.test.jsx). Most rows
  // are clean and a reviewer's eye is calibrated on them, so every added
  // element is conditional and the value itself is defined ONCE and composed
  // into whichever shape the row needs.
  const valueContent = (
    <>
      <span className={`rd-field-value ${!value ? 'rd-field-value--empty' : ''}`}>
        {displayValue ?? '—'}
      </span>
      {isCorrected && <span className="rd-field-modified-dot" title="Corrected" style={{ color: 'var(--stm-accent-info)' }}>✓</span>}
    </>
  );
  // The marker rides on the value's own line rather than the row, so it stays
  // beside the value when the reason wraps the area into two lines. It carries
  // no text of its own: the reason underneath is the accessible statement, and
  // a screen reader announcing a bullet before it would only be noise.
  const flag = flagged
    ? <span className="rd-field-flag" aria-hidden="true">●</span>
    : null;
  const decorated = flagged || !!reason;

  return (
    <div
      className={`rd-field-row${isCorrected ? ' rd-field-row--corrected' : ''}${isCorrecting ? ' rd-field-row--correcting' : ''}${isDragging ? ' rd-field-row--dragging' : ''}${isDragOver ? ' rd-field-row--dragover' : ''}`}
      onDragOver={dragEnabled ? onRowDragOver : undefined}
      onDrop={dragEnabled ? onRowDrop : undefined}
    >
      {dragEnabled && (
        <span
          className="rd-field-grip"
          draggable
          onDragStart={onGripDragStart}
          onDragEnd={onDragEnd}
          title="Drag to reorder"
          aria-label={`Reorder ${label}`}
        >
          ⋮⋮
        </span>
      )}
      {templateField ? (
        // A field only the applied template defines. Stacked under the label,
        // not beside it: the label column is fixed-width and truncates.
        <span className="rd-field-label-group">
          <span className="rd-field-label">{label}</span>
          <span
            className="rd-field-template-marker"
            title="Defined by this document's template, not the Field Library"
          >
            Template field
          </span>
        </span>
      ) : (
        <span className="rd-field-label">{label}</span>
      )}
      <div className={`rd-field-value-area${reason ? ' rd-field-value-area--stacked' : ''}`}>
        {decorated ? <span className="rd-field-value-line">{flag}{valueContent}</span> : valueContent}
        {reason && <span className="rd-field-reason">{reason}</span>}
      </div>
      <button
        type="button"
        className={`rd-field-correct-btn${isCorrecting ? ' rd-field-correct-btn--armed' : ''}`}
        onClick={() => onCorrect && onCorrect(fieldKey)}
        aria-pressed={isCorrecting || undefined}
        title={isCorrecting
          ? 'Click the value in the document — or click again to type the correction'
          : 'Correct this field'}
        aria-label={`Correct ${label}`}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M11.5 1.5l3 3-9 9-3.5.5.5-3.5 9-9z" />
          <path d="M10 3l3 3" />
        </svg>
      </button>
    </div>
  );
}

// ── Main component ──

const ReviewDetailPage = () => {
  const { executionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Queue-aware navigation: use queueDocIds (document IDs) for navigation
  const passedQueueDocIds = location.state?.queueDocIds || [];
  const passedSiblingIds = location.state?.siblingIds || [];
  const [queueDocIds, setQueueDocIds] = useState(passedQueueDocIds);
  const [totalQueueSize, setTotalQueueSize] = useState(passedQueueDocIds.length);
  const [approvedCount, setApprovedCount] = useState(0);

  // Legacy: siblingIds for workflow-based navigation
  const siblingIds = passedSiblingIds;
  const currentIndex = siblingIds.indexOf(executionId);

  // Queue position: find current document in queueDocIds
  // executionId may be a workflow execution ID or a document ID
  const [captureDocumentIdForQueue, setCaptureDocumentIdForQueue] = useState(null);
  const queueIndex = captureDocumentIdForQueue ? queueDocIds.indexOf(captureDocumentIdForQueue) : -1;
  const hasPrev = queueDocIds.length > 0 ? queueIndex > 0 : currentIndex > 0;
  const hasNext = queueDocIds.length > 0
    ? (queueIndex >= 0 && queueIndex < queueDocIds.length - 1)
    : (currentIndex >= 0 && currentIndex < siblingIds.length - 1);
  const queuePosition = queueIndex >= 0 ? queueIndex + 1 : (currentIndex >= 0 ? currentIndex + 1 : 0);
  const queueTotal = queueDocIds.length || siblingIds.length;

  // Loading / error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Set when this id turns out to be a split container, which is verified on
  // its own page (slice 3b). While set, this page renders nothing — its action
  // bar must never be offered for a container, not even for a frame.
  const [redirectingToSplitReview, setRedirectingToSplitReview] = useState(false);
  const [inlineError, setInlineError] = useState(null);
  // A re-extract that succeeded but whose results this page could not reload.
  const [reextractRefreshWarning, setReextractRefreshWarning] = useState(null);
  // An Approve that ended in a failed publish: the server's reason (and its
  // reference). It is shown ONCE, by the publish notice, which also has the
  // destination and the retry. Only when the notice is not on the page does it
  // fall back to the inline error.
  const [approvePublishFailure, setApprovePublishFailure] = useState(null);

  // Execution data
  const [execution, setExecution] = useState(null);
  const [workflowName, setWorkflowName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [nodeLabel, setNodeLabel] = useState('');
  // node id -> node type, from the workflow definition (null until loaded).
  const [workflowNodeTypes, setWorkflowNodeTypes] = useState(null);
  const [filename, setFilename] = useState('');
  const [mimeType, setMimeType] = useState('');
  const [documentType, setDocumentType] = useState(null);
  const [documentStatus, setDocumentStatus] = useState(null);
  const [aiCost, setAiCost] = useState(null);
  const [publishStatus, setPublishStatus] = useState(null);
  const [captureDocumentId, setCaptureDocumentId] = useState(null);
  // Which document is shown now, read when a late answer lands: an answer for
  // another document is never applied to this one.
  const captureDocumentIdRef = useRef(null);
  captureDocumentIdRef.current = captureDocumentId;
  // Slice 2b-3: the Vendor block's one source of truth. vendor_directory_in_use
  // is undefined until read (and when the read failed).
  const [vendorSnapshot, setVendorSnapshot] = useState(null);
  const [vendorDirectoryInUse, setVendorDirectoryInUse] = useState(undefined);
  const [appliedTemplateId, setAppliedTemplateId] = useState(null);

  // ── Document-type correction (v1a step 6) ──
  const [showTypePanel, setShowTypePanel] = useState(false);
  const [docTypeRows, setDocTypeRows] = useState(null); // registry rows, lazy-loaded on open
  const [loadingDocTypes, setLoadingDocTypes] = useState(false);
  const [selectedTypeKey, setSelectedTypeKey] = useState('');
  // { forType, status: 'loading'|'ready'|'error', data?, message? } — forType
  // lets confirmDisabled reject a preflight that answered for a prior selection.
  const [typePreflight, setTypePreflight] = useState(null);
  const [typeSaving, setTypeSaving] = useState(false);
  const [typeError, setTypeError] = useState(null);
  // Q9 supersession, both belts: abort the in-flight request AND sequence-count
  // so a straggler that already resolved cannot race a stale retainsAccess in.
  const typePreflightSeq = useRef(0);
  const typePreflightAbortRef = useRef(null);

  // ── Guided setup v2 slice 1b: turn one ──────────────────────────────────
  // What the classifier saw, kept off the document response so the panel can
  // explain the stored answer instead of re-deriving one. All three read as
  // null for a row that predates the columns (toJSON serves `?? null`), which
  // is exactly the state the panel must render as "unknown", not as zero.
  const [documentTypeSource, setDocumentTypeSource] = useState(null);
  const [documentTypeConfidence, setDocumentTypeConfidence] = useState(null);
  const [documentTypeEvidence, setDocumentTypeEvidence] = useState(null);
  // The PENDING declaration row, if one is already queued for this document.
  const [typeDeclaration, setTypeDeclaration] = useState(null);
  const [declaring, setDeclaring] = useState(false);
  const [declareError, setDeclareError] = useState(null);
  // ── Guided setup v2: the proposal ──────────────────────────────────────
  // The stored, server-authored proposal awaiting review. `proposalId` is what
  // apply references — the browser never posts a proposal body, because apply
  // mints tenant-global definitions and a body-carried proposal would let a
  // builder mint arbitrary schema from devtools.
  const [setupProposal, setSetupProposal] = useState(null);
  const [setupProposalId, setSetupProposalId] = useState(null);
  // Authoring takes one AI call, measured at 4-7 seconds. The reviewer must be
  // told something is happening or they press the button again.
  const [proposing, setProposing] = useState(false);
  const [proposeError, setProposeError] = useState(null);
  const [applyingSetup, setApplyingSetup] = useState(false);
  // A string, or { message, code } so a 400 phrases_required lands on the
  // keyword input rather than at the bottom of the panel.
  const [applyError, setApplyError] = useState(null);
  // null = nobody has decided, so the auto-open rule answers. true/false = the
  // reviewer opened it with "Why?" or closed it with "Yes, that's right".
  // Deliberately NOT persisted: dismissal is a local "I looked at this", not a
  // stored assertion about the document. Turn one writes nothing on "yes".
  const [evidencePanelOverride, setEvidencePanelOverride] = useState(null);

  // Builder-only "Create Template" CTA (Option A: one-click direct build,
  // editor is the confirmation step). Gated on canBuild + hasClaudeFields +
  // no template applied. canBuild reflects the bootstrap-resolved role.
  const canBuild = useCanBuildWorkflows() === true;
  // Retrying a failed step other than Publish is admin-only (the server
  // enforces it; this only hides the control from people it would refuse).
  const isAdmin = useIsAdmin();
  const [buildingTemplate, setBuildingTemplate] = useState(false);
  const [buildTemplateError, setBuildTemplateError] = useState(null);
  const buildingTemplateRef = useRef(false);

  // Fields: merged from execution context + persisted extracted fields
  const [fields, setFields] = useState({}); // { key: value }
  // Per-field verification state from capture_extracted_fields. Kept as flat
  // key-maps like sourceMap below, one entry per row the document has.
  const [fieldStatus, setFieldStatus] = useState({});   // { key: 'needs_review' | ... }
  const [fieldEvidence, setFieldEvidence] = useState({}); // { key: evidence blob }
  // Keys whose displayed value came from the execution context and DISAGREES
  // with the row the evidence was written about. The reason describes the
  // stored value; attaching it to a different number on screen would be a
  // sentence about something the reviewer cannot see.
  const [overlaidKeys, setOverlaidKeys] = useState({}); // { key: true }

  // Per-user Index-Field display ORDER (order-only preference; never a visibility
  // list). null = no saved preference, list renders in default order.
  const [fieldOrderPref, setFieldOrderPref] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [orderSaved, setOrderSaved] = useState(false);
  const [draggedKey, setDraggedKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  // Per-field extraction source ({ key: 'claude' | 'heuristic' | ... }) from the
  // RAW extracted-fields response (not library-filtered). Drives hasClaudeFields,
  // which gates the Create Template CTA (the build endpoint needs claude fields).
  const [sourceMap, setSourceMap] = useState({});
  // modifiedFields state was removed in Section 5. Field corrections now
  // flow through the Field Correction modal → POST .../corrections endpoint
  // and are applied server-side. No client-side "pending edits" buffer.

  // Line items
  const [lineItems, setLineItems] = useState([]);
  const [lineItemsExpanded, setLineItemsExpanded] = useState(false);
  const [lineItemsModalOpen, setLineItemsModalOpen] = useState(false);
  // Inline cell editing (Stage-2 Unit 2): one active cell at a time.
  const [editingCell, setEditingCell] = useState(null); // { lineItemId, columnKey }
  const [editingValue, setEditingValue] = useState('');
  const [editingError, setEditingError] = useState(null);
  const [savingCell, setSavingCell] = useState(false);
  const [modalPos, setModalPos] = useState({ x: null, y: null });
  const [appendModalOpen, setAppendModalOpen] = useState(false);
  const [appendFile, setAppendFile] = useState(null);
  const [appendWorking, setAppendWorking] = useState(false);
  const modalDragRef = useRef(null);
  const modalDragState = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });

  // PDF viewer document
  const [viewerDoc, setViewerDoc] = useState(null);

  // Action states
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [discardReason, setDiscardReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [reclassifying, setReclassifying] = useState(false);
  const [showReExtractPanel, setShowReExtractPanel] = useState(false);
  // Stage-2 Unit 3: confirm before re-extract discards manual corrections.
  const [showReExtractWarning, setShowReExtractWarning] = useState(false);
  const [vendorProfiles, setVendorProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Document Studio
  const [showDocumentStudio, setShowDocumentStudio] = useState(false);
  const [aiSplitSuggestions, setAiSplitSuggestions] = useState([]);

  // Extraction notes
  const [extractionNotes, setExtractionNotes] = useState('');
  const [correctionModal, setCorrectionModal] = useState(null);
  // correctionModal shapes (mutually compatible — extra fields are optional):
  //   - toolbar pencil + draw-rectangle: { zone, shape, fabricCanvas } (vestigial)
  //   - typed fallback (armed, no click): { initialField }
  //   - click-on-token:                   { correctedValue, valueBbox, pageNumber, initialField }
  // correctingField: the field currently armed for click-to-select in the
  // viewer. Set by the right-side per-field button; drives the viewer's
  // `correctingFieldKey` prop and the row's --correcting highlight. Cleared on
  // correction confirm/cancel.
  const [correctingField, setCorrectingField] = useState(null);
  const [correctedFields, setCorrectedFields] = useState({});
  // correctedFields: { field_key: corrected_value } — for visual indicator
  // 5-B: viewer rotation surfaces back here so the modal can show a "rotate
  // back to 0° to use click-to-select" hint when the page is rotated.
  const [viewerRotation, setViewerRotation] = useState(0);

  // Split panel
  const [splitPct, setSplitPct] = useState(62);

  // Phase 4 — Confidence flag
  const [flagging, setFlagging] = useState(false);
  const [showFlagReason, setShowFlagReason] = useState(false);
  const [flagReason, setFlagReason] = useState('');

  // Field definitions from API (for canonical labels and ordering)
  // Field definitions for this document's type and applied template, from
  // GET /field-definitions. Reset whenever either changes.
  const [fieldDefs, setFieldDefs] = useState({});

  // Phase 5 — Document links
  const [links, setLinks] = useState([]);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkType, setLinkType] = useState('matches_po');
  const [linkReference, setLinkReference] = useState('');
  const [linkNote, setLinkNote] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [notesProfileId, setNotesProfileId] = useState(null);
  const [notesVendorName, setNotesVendorName] = useState(null);
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Lock the document body to the viewport while the review page is mounted
  // so the inner PDF and right-panel scroll contexts contain their overflow
  // instead of pushing the document body past the viewport. See the
  // `body.app-viewport-locked` rules in index.css.
  useEffect(() => {
    document.body.classList.add('app-viewport-locked');
    return () => document.body.classList.remove('app-viewport-locked');
  }, []);

  // Workflow History (audit trail)
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [workflowHistoryExpanded, setWorkflowHistoryExpanded] = useState(false);
  const [retryingNodeId, setRetryingNodeId] = useState(null);
  const [retryingFailedStep, setRetryingFailedStep] = useState(false);
  const [rewindingToNodeId, setRewindingToNodeId] = useState(null);

  // ── Append document ──
  const handleAppend = useCallback(async () => {
    if (!appendFile) return;
    setAppendWorking(true);
    try {
      const formData = new FormData();
      formData.append('file', appendFile);
      const res = await capturePostForm(
        `/api/capture/processing-monitor/documents/${captureDocumentId}/append`,
        formData
      );
      if (!res.data?.ok) throw new Error(res.data?.error || 'Append failed');
      setToast({ type: 'success', message: `Appended ${res.data.pagesAdded} page(s). Re-processing...` });
      setAppendModalOpen(false);
      setAppendFile(null);
      setTimeout(() => navigate('/queue'), 2000);
    } catch (err) {
      setToast({ type: 'error', message: err.message || 'Append failed' });
    } finally {
      setAppendWorking(false);
    }
  }, [appendFile, captureDocumentId, navigate]);

  // ── Line items modal drag ──
  const handleModalMouseDown = (e) => {
    if (e.target.closest('.rd-line-items-modal-close')) return;
    const el = modalDragRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    modalDragState.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left,
      origY: rect.top,
    };
    const onMove = (me) => {
      if (!modalDragState.current.dragging) return;
      const dx = me.clientX - modalDragState.current.startX;
      const dy = me.clientY - modalDragState.current.startY;
      setModalPos({ x: modalDragState.current.origX + dx, y: modalDragState.current.origY + dy });
    };
    const onUp = () => {
      modalDragState.current.dragging = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    e.preventDefault();
  };

  // ── Queue refresh: reload the pending_review document list ──
  const refreshQueue = useCallback(async () => {
    try {
      const res = await captureAPI.processingMonitor.getDocuments({ limit: 200 });
      if (res.ok) {
        const reviewDocs = (res.documents || [])
          // A failed publish is under Errors, not in the review queue.
          .filter(d => ['needs_review', 'pending_review'].includes(d.status) && d.publishStatus !== 'failed')
          .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
        const newIds = reviewDocs.map(d => d.id);
        setQueueDocIds(newIds);
        setTotalQueueSize(prev => Math.max(prev, newIds.length + approvedCount));
      }
    } catch (err) {
      console.warn('[ReviewDetail] queue refresh failed:', err?.message);
    }
  }, [approvedCount]);

  // ── Navigate to a document by its ID in the queue ──
  const navigateToQueueDoc = useCallback((docId) => {
    navigate(`/review/${docId}`, {
      state: { ...location.state, queueDocIds, fromQueue: true },
      replace: false,
    });
  }, [navigate, location.state, queueDocIds]);

  const goToNextDoc = useCallback(() => {
    if (queueDocIds.length > 0 && queueIndex >= 0) {
      // Find next valid doc in queue (skip current, handle stale entries)
      for (let i = queueIndex + 1; i < queueDocIds.length; i++) {
        navigateToQueueDoc(queueDocIds[i]);
        return;
      }
      // No more docs — queue complete
      navigate('/queue');
    } else if (hasNext && siblingIds.length > 0) {
      navigate(`/review/${siblingIds[currentIndex + 1]}`, { state: location.state });
    }
  }, [queueDocIds, queueIndex, navigateToQueueDoc, navigate, hasNext, siblingIds, currentIndex, location.state]);

  const goToPrevDoc = useCallback(() => {
    if (queueDocIds.length > 0 && queueIndex > 0) {
      navigateToQueueDoc(queueDocIds[queueIndex - 1]);
    } else if (currentIndex > 0 && siblingIds.length > 0) {
      navigate(`/review/${siblingIds[currentIndex - 1]}`, { state: location.state });
    }
  }, [queueDocIds, queueIndex, navigateToQueueDoc, navigate, currentIndex, siblingIds, location.state]);

  // ── Keyboard shortcuts for queue navigation ──
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't intercept when typing in inputs/textareas or scrolling PDF
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.target.closest('.rd-viewer')) return;

      if (e.key === ']' || (e.key === 'ArrowRight' && !e.target.closest('.rd-viewer'))) {
        e.preventDefault();
        goToNextDoc();
      } else if (e.key === '[' || (e.key === 'ArrowLeft' && !e.target.closest('.rd-viewer'))) {
        e.preventDefault();
        goToPrevDoc();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNextDoc, goToPrevDoc]);

  // ── Load execution + workflow + document + fields ──
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    // Another document: the last one's failed Approve is not this one's.
    setApprovePublishFailure(null);
    setAiCost(null);
    setVendorSnapshot(null);
    setVendorDirectoryInUse(undefined);
    try {
      // 1. Get execution — fall back to document-direct mode if not found.
      // The AI fallback processing path sets pending_review with no workflow
      // execution record, so the route param may be a document ID instead.
      let docId = null;
      const execRes = await captureAPI.workflows.getExecution(executionId).catch(() => null);
      const exec = execRes?.ok && execRes.execution ? execRes.execution : null;

      if (exec) {
        // Normal workflow execution path
        // A FAILED run opens its document (read-only: no Approve, no Reject)
        // so the reviewer can see why it failed and retry the publish.
        // Completed and cancelled runs are "Already Reviewed", as before.
        // A run Re-extract replaced (cancelled) opens the run that replaced it.
        if (exec.status === 'cancelled' && exec.capture_document_id) {
          const replacing = await findReplacingRun(exec.capture_document_id, exec.id);
          if (replacing) {
            navigate(`/review/${replacing.id}`, {
              replace: true,
              state: { ...(location.state || {}), siblingIds: replaceSiblingId(location.state?.siblingIds, exec.id, replacing.id) },
            });
            return;
          }
        }
        if (exec.status !== 'paused' && exec.status !== 'failed') {
          setError(`already_reviewed:${exec.status}`);
          setLoading(false);
          return;
        }

        setExecution(exec);
        docId = exec.capture_document_id;
        setCaptureDocumentId(docId);
        setCaptureDocumentIdForQueue(docId);

        // 2. Load workflow (for name + instructions) and document metadata in parallel
        const [wfRes, docRes] = await Promise.all([
          captureAPI.workflows.get(exec.workflow_id).catch(() => null),
          captureAPI.getDocument(docId).catch(() => null),
        ]);

        // Workflow name + node instructions
        if (wfRes?.ok && wfRes.workflow) {
          setWorkflowName(wfRes.workflow.name || '');
          try {
            const def = typeof wfRes.workflow.definition === 'string'
              ? JSON.parse(wfRes.workflow.definition)
              : wfRes.workflow.definition || { nodes: [] };
            setWorkflowNodeTypes(Object.fromEntries((def.nodes || []).map((n) => [n.id, n.type])));
            const node = (def.nodes || []).find((n) => n.id === exec.current_node_id);
            if (node) {
              setNodeLabel(node.data?.label || '');
              setInstructions(node.data?.config?.instructions || '');
            }
          } catch { /* ignore */ }
        }

        // Document metadata
        const doc = docRes || {};
        setFilename(doc.filename || 'Unknown document');
        setMimeType(doc.mime_type || 'application/pdf');
        setDocumentType(doc.document_type || null);
        // `?? null` throughout — a 0 confidence is a real recorded value and
        // `|| null` would erase it into "never recorded".
        setDocumentTypeSource(doc.document_type_source ?? null);
        setDocumentTypeConfidence(doc.document_type_confidence ?? null);
        setDocumentTypeEvidence(doc.document_type_evidence ?? null);
        setDocumentStatus(doc.status || null);
        setPublishStatus(doc.publish_status || null);
        setAppliedTemplateId(doc.applied_template_id || null);
        setAiCost(doc.ai_cost ?? null);
        setVendorSnapshot(vendorSnapshotOf(doc));
        setVendorDirectoryInUse(doc.vendor_directory_in_use);

      } else {
        // Document-direct fallback path — executionId param is actually a
        // capture document ID. Hit by both the AI-fallback processing path
        // (sets pending_review with no execution) AND the /documents/:id
        // route used by the Completed tab's View button (published docs).
        // No workflow context available — load document metadata directly.
        docId = executionId;
        setCaptureDocumentId(docId);
        setCaptureDocumentIdForQueue(docId);
        setWorkflowName('');
        setNodeLabel('');
        setInstructions('');

        const docRes = await captureAPI.getDocument(docId).catch(() => null);
        const doc = docRes || {};
        // A split container is not a document to review: its pages were
        // distributed to children. It is verified on its own page (slice 3b),
        // and this page's action bar must not be offered for it — Reject here
        // PATCHes status with no current-status guard and would turn the
        // container into 'rejected'.
        // A container held for boundary review (slice 3c) is sent there too: it is
        // a batch whose documents do not exist yet, and this action bar would
        // re-extract, reject or approve the whole scan as one document.
        if (doc.status === 'split' || doc.status === 'pending_boundary_review') {
          setRedirectingToSplitReview(true);
          navigate(`/split-review/${docId}`, { replace: true });
          return;
        }
        // A document with a paused or failed workflow run is handled on that
        // run's page, where Approve resumes the run and Retry re-runs its
        // Publish node. Opened here it would be approved by publishing straight
        // to the default destination, around the workflow. Best-effort at load:
        // Approve and Retry check again, and refuse if they cannot tell.
        const liveRun = await findLiveRun(docId).catch(() => null);
        if (liveRun) {
          navigate(`/review/${liveRun.id}`, { replace: true, state: location.state });
          return;
        }
        setFilename(doc.filename || 'Unknown document');
        setMimeType(doc.mime_type || 'application/pdf');
        setDocumentType(doc.document_type || null);
        setDocumentTypeSource(doc.document_type_source ?? null);
        setDocumentTypeConfidence(doc.document_type_confidence ?? null);
        setDocumentTypeEvidence(doc.document_type_evidence ?? null);
        setDocumentStatus(doc.status || null);
        setPublishStatus(doc.publish_status || null);
        setAppliedTemplateId(doc.applied_template_id || null);
        setAiCost(doc.ai_cost ?? null);
        setVendorSnapshot(vendorSnapshotOf(doc));
        setVendorDirectoryInUse(doc.vendor_directory_in_use);
      }

      // 3. Load PDF signed URL
      try {
        const fileUrlRes = await captureAPI.getDocumentFileUrl(docId);
        setViewerDoc({
          id: docId,
          downloadUrl: fileUrlRes.url,
          filename: filename || 'document.pdf',
        });
      } catch {
        setViewerDoc(null);
      }

      // 4. Build fields: start from persisted extracted fields, overlay with execution context
      let persisted = mapExtractedFields([]);

      // Persisted fields from DB
      try {
        const efRes = await getExtractedFields(docId);
        if (efRes?.fields) persisted = mapExtractedFields(efRes.fields);
      } catch { /* tolerate */ }
      const mergedFields = { ...persisted.fields };
      // What the DB row itself displayed, before any overlay. The reason is a
      // statement about THIS string.
      const persistedValues = persisted.fields;

      // Execution context fields take precedence (skipped on document-direct fallback path)
      const overlaidLocal = {};
      if (exec) {
        const ctx = typeof exec.context === 'string'
          ? JSON.parse(exec.context)
          : exec.context || {};
        if (ctx.extractedFields && typeof ctx.extractedFields === 'object') {
          for (const [k, v] of Object.entries(ctx.extractedFields)) {
            const next = v != null ? String(v) : '';
            // The overlay carries values only — no status, no evidence. Where
            // it agrees with the stored row the reason still describes what is
            // on screen; where it OVERWRITES the value, the evidence is about
            // a number the reviewer is no longer looking at, so the reason is
            // dropped rather than misattributed.
            if (persistedValues[k] !== undefined && next !== persistedValues[k]) {
              overlaidLocal[k] = true;
            }
            mergedFields[k] = next;
          }
        }
      }

      setFields(mergedFields);
      setSourceMap(persisted.sourceMap);
      setFieldStatus(persisted.statusMap);
      setFieldEvidence(persisted.evidenceMap);
      setOverlaidKeys(overlaidLocal);

      // 5. Load line items
      try {
        const liRes = await getLineItems(docId);
        if (liRes?.ok && Array.isArray(liRes.items)) {
          setLineItems(liRes.items);
        } else {
          setLineItems([]);
        }
      } catch { setLineItems([]); }

      // 6. Load existing vendor extraction notes
      try {
        const notesRes = await captureGet(`/api/capture/documents/${docId}/extraction-notes`);
        if (notesRes.data?.ok) {
          setExtractionNotes(notesRes.data.notes || '');
          setNotesProfileId(notesRes.data.profileId || null);
          setNotesVendorName(notesRes.data.vendorName || null);
        }
      } catch (e) {
        console.warn('[ReviewDetail] failed to load extraction notes:', e?.message);
      }

      // 7. Load workflow step history if execution exists
      if (exec?.id) {
        try {
          const stepsRes = await captureAPI.workflows.getExecutionSteps(exec.id);
          if (stepsRes?.ok && Array.isArray(stepsRes.steps)) {
            setWorkflowSteps(stepsRes.steps);
          }
        } catch (e) {
          console.warn('[ReviewDetail] failed to load workflow steps:', e?.message);
        }
      }

      // 8. Is a type declaration already queued for this document? Read INSIDE
      // the try (before `loading` clears) so the auto-open rule sees the answer
      // at first paint rather than opening the panel and closing it again.
      // Non-fatal: a declaration read must never block Review — the route is
      // inert until its migration is applied, and a reviewer with a broken
      // declaration read still needs to review.
      setTypeDeclaration(null);
      setDeclareError(null);
      setEvidencePanelOverride(null);
      setSetupProposal(null);
      setSetupProposalId(null);
      setProposeError(null);
      setApplyError(null);
      try {
        const declRes = await captureAPI.getTypeDeclaration(docId);
        const declaration = declRes?.declaration ?? null;
        setTypeDeclaration(declaration);

        // Only when a declaration is actually pending. Every other document in
        // the queue would otherwise pay for a read that can only return null.
        if (declaration) {
          try {
            const propRes = await captureAPI.getSetupProposal(docId);
            setSetupProposal(propRes?.proposal ?? null);
            setSetupProposalId(propRes?.proposal_id ?? null);
          } catch (e) {
            // Non-fatal, same posture as the declaration read: a reviewer with
            // a broken proposal read still needs to review the document. The
            // "Continue setup" affordance simply re-authors one.
            console.warn('[ReviewDetail] failed to load setup proposal:', e?.message);
          }
        }
      } catch (e) {
        console.warn('[ReviewDetail] failed to load type declaration:', e?.message);
      }
    } catch (err) {
      setError(err.message || 'Failed to load review data');
    } finally {
      setLoading(false);
    }
  }, [executionId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Per-field correction entry point ──
  // Sole correction affordance. First click ARMS the viewer's OCR-word
  // click-to-select overlay for this field (setCorrectingField); the user then
  // clicks the correct value token, which fires handleTokenClicked → opens the
  // confirm modal pre-scoped to this field with the clicked value + inferred
  // anchor. A second click on the SAME armed field is the type-in fallback for
  // when OCR missed the value / there is no clickable token: it opens the modal
  // directly with no bbox (→ region_only) and disarms.
  //
  // Both paths POST to /api/capture/processing-monitor/documents/:id/corrections
  // which writes capture_extracted_fields and vendor_profile_observations.
  const handlePerFieldCorrect = useCallback((fieldKey) => {
    if (correctingField === fieldKey) {
      // Already armed → typed fallback (no bbox). `locked: true` pins the modal
      // to this field: the user chose it by pressing its button, and a typed
      // correction must land on that field. (No anchor here, but retargeting a
      // deliberately-armed correction is still wrong.)
      setCorrectingField(null);
      setCorrectionModal({ initialField: fieldKey, locked: true });
      return;
    }
    // Arm the viewer for click-to-select. Guidance (including the rotated-page
    // "rotate to 0° or type" case) is surfaced by the viewer's armed-state
    // banner, not a toast.
    setCorrectingField(fieldKey);
  }, [correctingField]);

  // Field Library labels, for reasons that name a sibling amount ("subtotal
  // $3,303.71"). Read from the same definitions the rows are labelled from, so
  // a tenant that renamed a field reads its own word in the sentence.
  const fieldLabelsByKey = useMemo(() => fieldDefLookups(fieldDefs).labels, [fieldDefs]);

  /**
   * The sentence under one field's value, or null for a row that gets none.
   *
   * Computed at render rather than at load: the Field Library definitions the
   * labels come from arrive in their own request, often after the fields do.
   *
   * Two suppressions, both about not attributing a reason to a value it was
   * not written about — a corrected field (the reviewer has already replaced
   * the value the evidence describes) and an execution-context overlay that
   * disagrees with the stored row.
   */
  const reasonForField = useCallback((key) => {
    if (key in correctedFields) return null;
    if (overlaidKeys[key]) return null;
    return fieldReason({
      status: fieldStatus[key],
      evidence: fieldEvidence[key],
      valueText: fields[key],
      fieldLabel: fieldLabelsByKey[key],
      siblingLabels: fieldLabelsByKey,
      source: sourceMap[key],
    });
  }, [correctedFields, overlaidKeys, fieldStatus, fieldEvidence, fields, fieldLabelsByKey, sourceMap]);

  const { keys: sortedFieldKeys, templateOnly: templateOnlyKeys } = useMemo(
    () => buildIndexFieldKeys({ fieldDefs, fields, sourceMap, appliedTemplateId, fieldOrderPref }),
    [fieldDefs, fields, sourceMap, appliedTemplateId, fieldOrderPref],
  );

  // ── Index-Field reorder (drag the ⋮⋮ grip) ──
  // Reordering is a DISPLAY preference and works regardless of document status
  // (including published / read-only). The only gate is a resolved doc type,
  // which scopes the saved preference.
  const persistFieldOrder = useCallback(async (newOrder, prevPref) => {
    setSavingOrder(true);
    try {
      await captureAPI.saveFieldPreferences({ docType: documentType, visibleKeys: newOrder });
      setOrderSaved(true);
      setTimeout(() => setOrderSaved(false), 2000);
    } catch (err) {
      setFieldOrderPref(prevPref); // revert optimistic update
      setToast({ type: 'error', message: err?.message || 'Failed to save field order' });
    } finally {
      setSavingOrder(false);
    }
  }, [documentType]);

  const reorderFields = useCallback((fromKey, toKey) => {
    if (!documentType || !fromKey || fromKey === toKey) return;
    const current = sortedFieldKeys;
    const fromIdx = current.indexOf(fromKey);
    if (fromIdx === -1 || current.indexOf(toKey) === -1) return;
    const next = [...current];
    next.splice(fromIdx, 1);
    next.splice(next.indexOf(toKey), 0, fromKey);
    const prevPref = fieldOrderPref;
    setFieldOrderPref(next); // optimistic; persist may revert
    persistFieldOrder(next, prevPref);
  }, [documentType, sortedFieldKeys, fieldOrderPref, persistFieldOrder]);

  const handleFieldDragStart = useCallback((e, key) => {
    setDraggedKey(key);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      try { e.dataTransfer.setData('text/plain', key); } catch (_) { /* older-browser guard */ }
    }
  }, []);

  const handleFieldDragOver = useCallback((e, key) => {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    setDragOverKey(key);
  }, []);

  const handleFieldDrop = useCallback((e, key) => {
    e.preventDefault();
    const from = draggedKey;
    setDraggedKey(null);
    setDragOverKey(null);
    if (from) reorderFields(from, key);
  }, [draggedKey, reorderFields]);

  const handleFieldDragEnd = useCallback(() => {
    setDraggedKey(null);
    setDragOverKey(null);
  }, []);

  const handleResetOrder = useCallback(async () => {
    if (!documentType) return;
    const prevPref = fieldOrderPref;
    setSavingOrder(true);
    try {
      await captureAPI.resetFieldPreferences({ docType: documentType });
      setFieldOrderPref(null); // back to default order
    } catch (err) {
      setFieldOrderPref(prevPref);
      setToast({ type: 'error', message: err?.message || 'Failed to reset field order' });
    } finally {
      setSavingOrder(false);
    }
  }, [documentType, fieldOrderPref]);

  // Derived gate for the Create Template CTA — computed from RAW per-field
  // sources (sourceMap), not any library-filtered subset.
  const hasClaudeFields = Object.values(sourceMap).some((s) => s === 'claude');

  // One-click "Create Template" from this document's Claude extraction (Option A).
  // Builds via the existing endpoint, then lands in the editor to confirm zones
  // and publish — the editor IS the confirmation step (no separate modal).
  const handleCreateTemplate = useCallback(async () => {
    if (buildingTemplateRef.current) return; // guard rapid double-click → no duplicate POST
    buildingTemplateRef.current = true;
    setBuildingTemplate(true);
    setBuildTemplateError(null);

    const docId = captureDocumentId || executionId;
    const baseName = (filename || '').replace(/\.[^.]+$/, '');
    const templateName = baseName || `Template ${new Date().toISOString().slice(0, 10)}`;

    // Pass the document's actual type (e.g. purchase_order). If missing/unknown,
    // omit so the endpoint applies its default, and log it.
    const knownDocType = documentType && documentType !== 'unknown' ? documentType : undefined;
    if (!knownDocType) {
      console.warn(`[ReviewDetail] create-template: document_type missing/unknown (got ${documentType ?? 'null'}) — omitting, endpoint will default`);
    }

    try {
      const options = { templateName };
      if (knownDocType) options.documentType = knownDocType;
      const res = await buildTemplateFromExtraction(docId, options);
      const templateId = res?.templateId;
      if (!templateId) throw new Error('Template created but no ID returned');
      navigate('/template-studio?openTemplate=' + templateId);
    } catch (err) {
      console.error('[ReviewDetail] create-template failed:', err);
      const msg = err?.response?.data?.error || err?.message || 'Failed to create template from extraction';
      setBuildTemplateError(msg);
    } finally {
      buildingTemplateRef.current = false;
      setBuildingTemplate(false);
    }
  }, [captureDocumentId, executionId, filename, documentType, navigate]);

  const handleCorrectionDrawn = (zone, shape, fabricCanvas) => {
    setCorrectionModal({ zone, shape, fabricCanvas });
  };

  // 5-B / 5-C: Click-on-token entry point. The viewer renders an OCR-word
  // overlay (armed via correctingFieldKey) when rotation === 0. A single click
  // selects one word; click-and-drag selects multiple words (5-C). On release,
  // the union bbox is computed and anchor inference walks left (then above as
  // fallback) to find a label-stop token. All four fields arrive together in
  // this callback and are stashed in correctionModal — pinned to the armed
  // field (correctingField) so the modal opens pre-scoped to it — for the POST.
  const handleTokenClicked = useCallback(({ text, bbox, page, anchorText, anchorBbox, valuePosition }) => {
    setCorrectionModal({
      correctedValue: text,
      valueBbox: bbox,        // { x1, y1, x2, y2 } normalized 0–1
      pageNumber: page,
      anchorText: anchorText ?? null,
      anchorBbox: anchorBbox ?? null, // D1: union bbox of the label phrase
      valuePosition: valuePosition ?? 'region_only',
      initialField: correctingField,
      // `locked: true` pins the modal to the armed field. Critical for the
      // clicked path: the payload carries THIS field's inferred anchor, so
      // retargeting to another field would teach that field a poisoned anchor.
      locked: true,
    });
  }, [correctingField]);

  // ── Phase 4: Confidence flag ──────────────────────────────────────────────
  const handleFlagAndApprove = async () => {
    if (!captureDocumentId) return;
    setFlagging(true);
    try {
      const headers = await getAuthHeaders('application/json');
      const res = await fetch(
        `${CAPTURE_API_BASE}/api/capture/processing-monitor/documents/${captureDocumentId}/flag`,
        { method: 'PATCH', headers, body: JSON.stringify({ flag_reason: flagReason }) }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || `Flag request failed (${res.status})`);
      }
      setShowFlagReason(false);
      setFlagReason('');
      handleApprove();
    } catch (err) {
      console.error('[flag] error:', err);
      setToast({ type: 'error', message: err.message || 'Failed to flag document' });
    } finally {
      setFlagging(false);
    }
  };

  // ── Phase 5: Document links ───────────────────────────────────────────────
  // Load field definitions from the capture API when document type is known
  // The applied template's own fields come back in the same response, tagged
  // origin 'template', so a field only the template defines is reviewable.
  useEffect(() => {
    setFieldDefs({});
    // Wait until we have a document type before fetching field definitions
    if (!documentType) return;
    let cancelled = false;

    const params = new URLSearchParams({ doc_type: documentType });
    if (appliedTemplateId) params.set('template_id', appliedTemplateId);
    const url = `${CAPTURE_API_BASE}/api/capture/processing-monitor/field-definitions?${params}`;

    getAuthHeaders().then(headers => fetch(url, { headers }))
      .then(r => r.json())
      .then(data => {
        if (!cancelled && data.ok && data.fields) setFieldDefs(data.fields);
      })
      .catch(() => { /* silent — fallback labels used */ });
    return () => { cancelled = true; };
  }, [captureDocumentId, documentType, appliedTemplateId]);

  // Load the caller's saved Index-Field display order for this doc type. Failure
  // is non-fatal — leave the preference null so the list stays in default order.
  useEffect(() => {
    if (!documentType) return;
    let cancelled = false;
    captureAPI
      .getFieldPreferences({ docType: documentType })
      .then((res) => { if (!cancelled) setFieldOrderPref(res?.visibleKeys ?? null); })
      .catch((err) => {
        console.warn('[ReviewDetail] getFieldPreferences failed:', err?.message);
        if (!cancelled) setFieldOrderPref(null);
      });
    return () => { cancelled = true; };
  }, [documentType]);

  const loadLinks = useCallback(async (docId) => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${CAPTURE_API_BASE}/api/capture/processing-monitor/documents/${docId}/links`, { headers });
      const data = await res.json();
      if (data.ok) setLinks(data.links || []);
    } catch { /* silent */ }
  }, []);

  // Load links after definition — avoids temporal dead zone
  useEffect(() => {
    if (SHOW_DOCUMENT_LINKS && captureDocumentId) loadLinks(captureDocumentId);
  }, [captureDocumentId, loadLinks]);

  const handleAddLink = async () => {
    if (!linkReference.trim() || !captureDocumentId) return;
    setSavingLink(true);
    try {
      const headers = await getAuthHeaders('application/json');
      const res = await fetch(
        `${CAPTURE_API_BASE}/api/capture/processing-monitor/documents/${captureDocumentId}/links`,
        { method: 'POST', headers, body: JSON.stringify({ link_type: linkType, target_reference: linkReference, note: linkNote }) }
      );
      const data = await res.json();
      if (data.ok) {
        setLinks(prev => [data.link, ...prev]);
        setLinkReference('');
        setLinkNote('');
        setShowLinkForm(false);
      }
    } catch (err) { console.error('[links] add error:', err); }
    finally { setSavingLink(false); }
  };

  const handleRemoveLink = async (linkId) => {
    try {
      const headers = await getAuthHeaders();
      await fetch(`${CAPTURE_API_BASE}/api/capture/processing-monitor/documents/${captureDocumentId}/links/${linkId}`, { method: 'DELETE', headers });
      setLinks(prev => prev.filter(l => l.id !== linkId));
    } catch { /* silent */ }
  };

  // Slice 2b-3: a vendor snapshot from a write, a correction or a re-read,
  // applied only while its document is the one shown.
  const applyVendorSnapshot = useCallback((docId, src) => {
    if (!src || !docId || captureDocumentIdRef.current !== docId) return;
    setVendorSnapshot(vendorSnapshotOf(src));
  }, []);

  // A vendor write refused as published: the document was published since it
  // was opened. Read it again so the page (and the block) turn read-only.
  const refreshAfterPublishedRefusal = useCallback(async (docId) => {
    const doc = await captureAPI.getDocument(docId).catch(() => null);
    if (!doc || captureDocumentIdRef.current !== docId) return;
    setDocumentStatus(doc.status || null);
    setPublishStatus(doc.publish_status || null);
    applyVendorSnapshot(docId, doc);
  }, [applyVendorSnapshot]);

  const handleCorrectionConfirm = async ({ field_key, corrected_value, typedInstead = false }) => {
    const sentFor = captureDocumentId;
    try {
      const headers = await getAuthHeaders('application/json');

      // D1: location evidence (value/anchor bbox, anchor phrase, position,
      // page) is sent only while the submitted value is still the clicked
      // text; an edited or typed value is sent as located_by 'typed' with no
      // location. See utils/correctionPayload.js.
      const body = buildCorrectionBody(correctionModal, { field_key, corrected_value, typedInstead });

      const res = await fetch(
        `${CAPTURE_API_BASE}/api/capture/processing-monitor/documents/${captureDocumentId}/corrections`,
        { method: 'POST', headers, body: JSON.stringify(body) }
      );

      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || `Correction failed (${res.status})`);
      }

      // Update local fields state so panel shows corrected value immediately
      setFields(prev => ({ ...prev, [field_key]: corrected_value }));
      setCorrectedFields(prev => ({ ...prev, [field_key]: corrected_value }));
      // A vendor_name correction re-keys the vendor stamp; the answer carries
      // the new snapshot (absent when its read failed: the block keeps its own).
      if (field_key === 'vendor_name' && data.vendor) applyVendorSnapshot(sentFor, data.vendor);

      // Remove the correction zone box from the canvas (only present in the
      // toolbar-pencil flow).
      if (correctionModal?.fabricCanvas && correctionModal?.shape) {
        correctionModal.fabricCanvas.remove(correctionModal.shape);
        correctionModal.fabricCanvas.renderAll();
      }
    } catch (err) {
      console.error('[correction] save failed:', err);
      setToast({ type: 'error', message: err.message || 'Failed to save correction' });
    } finally {
      setCorrectionModal(null);
      setCorrectingField(null); // disarm the viewer overlay
    }
  };

  // Documents reached via /documents/:id (Completed tab "View") arrive with
  // documentStatus='published'. The page renders read-only for those: workflow
  // actions (Approve & Continue, Reject, Flag) are hidden because the doc is
  // already through the workflow. Re-extract and per-field corrections remain
  // available — operators can still fix things post-publish, and the
  // corrections handler is status-agnostic so the observation pipeline works.
  const isPublished = documentStatus === 'published';
  // A run that has ended without publishing (failed) has nothing left to
  // approve or reject: the page is read-only in the same sense as a published
  // document's, with the failure and its retry shown by PublishStatusNotice.
  const runEnded = Boolean(execution) && execution.status !== 'paused';
  // The publish failed if the document says so OR the run failed at its
  // Publish node. Either is enough; a published document has neither.
  const publishFailed = !isPublished && (publishStatus === 'failed' || runFailedAtPublish(execution, workflowNodeTypes));
  const effectivePublishStatus = publishFailed ? 'failed' : publishStatus;
  const topbarPill = statusPill({ documentStatus, execution, publishFailed });
  // "Retry failed step": an admin, a failed run, and a failed step that is not
  // Publish (a failed publish has its own notice and retry, open to members).
  const failedStep = failedStepOf(execution, workflowSteps, workflowNodeTypes);
  const canRetryFailedStep =
    isAdmin && Boolean(failedStep) && !isPublished && !publishFailed && failedStep.type !== 'output_publish';

  // ── Inline line-item cell editing (Stage-2 Unit 2) ──────────────────────
  // Editing is allowed in all review states (incl. published) to match the
  // status-agnostic per-field correction precedent. Saves go through the
  // department-gated Unit-1 PUT; on success we refetch so the server-stamped
  // source='manual'/confidence land.
  // A refetch error keeps the current rows. With throwOnError the caller is
  // told, an ok:false answer included.
  const refetchLineItems = useCallback(async ({ throwOnError = false } = {}) => {
    try {
      const liRes = await getLineItems(captureDocumentId);
      if (liRes?.ok && Array.isArray(liRes.items)) {
        setLineItems(liRes.items);
      } else if (throwOnError) {
        throw new Error(liRes?.error || 'line items response was not ok');
      }
    } catch (err) {
      if (throwOnError) throw err;
    }
  }, [captureDocumentId]);

  const beginCellEdit = (item, columnKey) => {
    if (!isEditableLineItemColumn(columnKey)) return;
    setEditingCell({ lineItemId: item.id, columnKey });
    setEditingValue(lineItemInputSeed(item[columnKey]));
    setEditingError(null);
  };

  // Set when Escape cancels, so the blur that fires as the input unmounts does
  // not re-trigger a save.
  const suppressBlurSaveRef = useRef(false);

  const cancelCellEdit = () => {
    suppressBlurSaveRef.current = true;
    setEditingCell(null);
    setEditingValue('');
    setEditingError(null);
  };

  const commitCellEdit = async () => {
    if (suppressBlurSaveRef.current) { suppressBlurSaveRef.current = false; return; }
    if (!editingCell || savingCell) return;
    const { lineItemId, columnKey } = editingCell;
    const coerced = coerceLineItemValue(columnKey, editingValue);
    if (!coerced.ok) {
      setEditingError(coerced.error); // keep the input open, do not save
      return;
    }
    setSavingCell(true);
    setEditingError(null);
    try {
      await updateLineItem(captureDocumentId, lineItemId, { [columnKey]: coerced.value });
      await refetchLineItems();
      setEditingCell(null);
      setEditingValue('');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Save failed';
      setEditingError(String(msg)); // keep the edit open
    } finally {
      setSavingCell(false);
    }
  };

  const handleCellKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitCellEdit(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelCellEdit(); }
  };

  const handleCorrectionCancel = () => {
    // Remove the drawn zone box from canvas on cancel (toolbar-pencil flow only).
    if (correctionModal?.fabricCanvas && correctionModal?.shape) {
      correctionModal.fabricCanvas.remove(correctionModal.shape);
      correctionModal.fabricCanvas.renderAll();
    }
    setCorrectionModal(null);
    setCorrectingField(null); // disarm the viewer overlay
  };

  // ── Workflow history handlers ──
  const handleRetryNode = async (nodeId) => {
    if (!execution?.id) return;
    setRetryingNodeId(nodeId);
    try {
      const result = await captureAPI.workflows.retryNode(execution.id, nodeId);
      if (result?.ok) {
        setToast({ type: 'success', message: 'Node retry initiated. Reloading...' });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setToast({ type: 'error', message: result?.error || 'Retry failed' });
      }
    } catch (err) {
      console.error('[retry-node] error:', err);
      setToast({ type: 'error', message: retryRefusalText(err, 'Failed to retry node') });
    } finally {
      setRetryingNodeId(null);
    }
  };

  // Re-run the run from the step it failed at (admin-only; any step but a
  // split). The server picks the node. The run is read again afterwards
  // whatever happened: it may have completed, paused for review or failed anew.
  const handleRetryFailedStep = async () => {
    if (!execution?.id || retryingFailedStep) return;
    setRetryingFailedStep(true);
    try {
      const result = await captureAPI.workflows.retryFailedNode(execution.id);
      if (result?.ok) {
        setToast({ type: 'success', message: 'Step retried.' });
      } else {
        setToast({ type: 'error', message: retryRefusalText(result, 'The retry failed.') });
      }
      await loadData();
    } catch (err) {
      console.error('[retry-failed-node] error:', err);
      setToast({ type: 'error', message: retryRefusalText(err, 'Could not retry the failed step.') });
    } finally {
      setRetryingFailedStep(false);
    }
  };

  const handleRewindExecution = async (targetNodeId) => {
    if (!execution?.id) return;
    setRewindingToNodeId(targetNodeId);
    try {
      const result = await captureAPI.workflows.rewindExecution(execution.id, targetNodeId);
      if (result?.ok) {
        setToast({ type: 'success', message: 'Execution rewound. Reloading...' });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setToast({ type: 'error', message: result?.error || 'Rewind failed' });
      }
    } catch (err) {
      console.error('[rewind] error:', err);
      setToast({ type: 'error', message: 'Failed to rewind execution' });
    } finally {
      setRewindingToNodeId(null);
    }
  };

  // ── Extraction notes save handler ──
  const handleSaveNotes = async () => {
    if (!notesProfileId) return;
    setNotesSaving(true);
    try {
      await capturePatch(`/api/capture/documents/${executionId}/extraction-notes`, { notes: extractionNotes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch (e) {
      console.warn('[ReviewDetail] failed to save extraction notes:', e?.message);
    } finally {
      setNotesSaving(false);
    }
  };

  // ── Document Studio handlers ──
  const handleOpenDocumentStudio = async () => {
    if (!captureDocumentId) return;
    
    try {
      const response = await captureGet(`/api/documents/${captureDocumentId}/split-suggestions`);
      
      if (response.data?.ok && response.data.suggestions) {
        setAiSplitSuggestions(response.data.suggestions);
      }
    } catch (err) {
      console.warn('[ReviewDetail] Failed to load split suggestions:', err?.message);
    }
    
    setShowDocumentStudio(true);
  };

  const handleDocumentStudioApply = async (operations) => {
    if (!captureDocumentId) return;
    
    try {
      const response = await capturePost(`/api/capture/documents/${captureDocumentId}/restructure`, { operations });
      
      if (response.data?.ok) {
        const childIds = response.data.childDocumentIds || [];
        setShowDocumentStudio(false);

        if (response.data.workflowOrphaned) {
          // The split succeeded but nothing will process the pieces: the
          // parent carried no workflow, so the children had none to inherit.
          // Saying "success" here would be a lie — they are not in the queue
          // and nothing is coming for them.
          setToast({
            type: 'warning',
            message: `Split into ${childIds.length} documents, but they are NOT queued — ` +
              `this document has no workflow, so nothing will classify or extract them.`,
          });
          setTimeout(() => loadData(), 500);
          return;
        }

        // The children are queued, not finished: their classification and
        // extraction run in the background, exactly as an AI split's children
        // do, and surface in the review queue when they get there.
        setToast({
          type: 'success',
          message: response.data.message || 'Document restructured successfully',
        });

        if (childIds.length > 0) {
          // This document is now a batch container: it has left the review
          // queue and its PDF no longer represents anything a reviewer acts
          // on. Reloading it would strand them on a shell. Go to the first
          // piece instead.
          navigate(`/review/${childIds[0]}`, { state: location.state });
          return;
        }

        // No split — the document was rotated or had pages deleted in place.
        setTimeout(() => {
          loadData();
        }, 500);
      }
    } catch (err) {
      console.error('[ReviewDetail] Document Studio apply failed:', err);
      setToast({ 
        type: 'error', 
        message: err?.response?.data?.error || 'Failed to apply changes' 
      });
    }
  };

  const handleDocumentStudioCancel = () => {
    setShowDocumentStudio(false);
    setAiSplitSuggestions([]);
  };

  // ── Approve flow ──
  // Section 5 simplified this: corrections now flow through the modal
  // endpoint, not through the approve payload. Approve resumes the workflow
  // (or publishes directly) with no field payload — the server reads
  // capture_extracted_fields, which the corrections endpoint has already
  // updated. The previous fallback POST to /workflow-executions/:id/review-fields
  // was 404ing silently in production (server only exposes that route as
  // PATCH); deleted along with the modifiedFields branch.
  const handleApprove = useCallback(async () => {
    // A run that is no longer paused has nothing to approve (the flag shortcut
    // reaches here too, not only the button).
    if (execution && execution.status !== 'paused') return;
    setApproving(true);
    setInlineError(null);
    setApprovePublishFailure(null);
    // Approve stays disabled from success until the page moves on (below).
    let approved = false;
    try {
      if (execution) {
        // axios throws on a 4xx/5xx; the server's body (its code and its own
        // error text) is what the reviewer must see, not "Request failed with
        // status code 409".
        const resumeRes = await captureAPI.workflows.resumeExecution(execution.id).catch((e) => {
          const data = e?.response?.data;
          if (data && typeof data === 'object') return data;
          throw e;
        });
        if (!resumeRes.ok) {
          const failure = new Error(resumeRes.error || 'Failed to resume workflow');
          failure.code = resumeRes.code;
          failure.errorId = resumeRes.error_id;
          failure.failedNode = resumeRes.failed_node || null;
          throw failure;
        }
        setToast({ type: 'success', message: 'Approved! Workflow continuing...' });
      } else {
        // Document-direct fallback: no workflow execution. Publish directly
        // to the default destination.
        //
        // ONLY for a document that has no workflow run at all, confirmed now.
        // A document with a run is approved by resuming that run; publishing it
        // from here would skip the workflow's Publish node, its destination and
        // its field selection. If the check cannot be made, nothing is published.
        const run = await findLatestRun(captureDocumentId);
        if (run) {
          throw new Error(
            `This document belongs to a workflow run (${run.status}), so it cannot be approved from this page. Open it from the Processing Monitor.`,
          );
        }
        const destRes = await captureGet('/api/capture/destinations');
        const destinations = destRes.data?.destinations || [];
        const defaultDest = destinations.find(d => d.is_default && d.is_active);

        if (!defaultDest) {
          throw new Error('No default destination configured. Please set a default destination in Settings → Destinations before approving.');
        }

        const publishRes = await capturePost(
          `/api/capture/documents/${captureDocumentId}/publish`,
          { destinationId: defaultDest.id }
        );

        if (!publishRes.data?.ok) {
          throw new Error(publishRes.data?.error || 'Publish failed');
        }

        setToast({ type: 'success', message: `Approved & published to ${defaultDest.name}.` });
      }

      setApprovedCount(prev => prev + 1);
      // Refresh queue to pick up changes, then navigate to next
      await refreshQueue();
      approved = true;
      setTimeout(() => {
        // Find next document in updated queue (skip current doc which was just approved)
        const updatedQueue = queueDocIds.filter(id => id !== captureDocumentId);
        if (queueIndex >= 0 && updatedQueue.length > 0) {
          const nextIdx = Math.min(queueIndex, updatedQueue.length - 1);
          setApproving(false);
          navigateToQueueDoc(updatedQueue[nextIdx]);
        } else if (hasNext && siblingIds.length > 0) {
          setApproving(false);
          navigate(`/review/${siblingIds[currentIndex + 1]}`, { state: location.state });
        } else {
          // Leaving for the queue: Approve stays disabled until then.
          setToast({ type: 'success', message: 'Queue complete! All documents reviewed.' });
          setTimeout(() => navigate('/queue'), 1200);
        }
      }, 600);
    } catch (err) {
      const withId = (text) => (err.errorId ? `${text} (error_id: ${err.errorId})` : text);
      if (err.code === 'publish_failed') {
        // Not the inline error: the publish notice below says it. (Both used to
        // show, one above the other, with the same reason.)
        setInlineError(null);
        setApprovePublishFailure({ reason: err.message || 'Publish failed', errorId: err.errorId || null });
      } else if (err.code === 'resume_in_progress') {
        // Another Approve (or a restart) got there first. Nothing was done by
        // this one; the re-read below shows the run as it now is.
        setApprovePublishFailure(null);
        setInlineError('This document was already approved a moment ago and its workflow is continuing. Nothing more was done.');
      } else if (err.code === 'node_failed') {
        // The run stopped at a step that is not Publish: say which, and why,
        // and who can retry it.
        setApprovePublishFailure(null);
        setInlineError(approveNodeFailedText(err, isAdmin));
      } else {
        setApprovePublishFailure(null);
        setInlineError(withId(err.message || 'Approve failed'));
      }
      // The execution is KEPT. (It used to be nulled here, which hid this very
      // error and turned the next Approve into a document-direct publish.)
      // A resume that failed has ended the run: read the run and the document
      // again, so Approve is withdrawn and the publish failure shows.
      if (execution) {
        const [execRes, docRes] = await Promise.all([
          captureAPI.workflows.getExecution(execution.id).catch(() => null),
          captureAPI.getDocument(captureDocumentId).catch(() => null),
        ]);
        const fresh = execRes?.ok && execRes.execution ? execRes.execution : null;
        // Re-extracted elsewhere (another tab, the Processing Monitor): this run
        // was cancelled and replaced. Say so, and open the current run.
        if (fresh?.status === 'cancelled') {
          const replacing = await findReplacingRun(captureDocumentId, execution.id);
          if (replacing) {
            setApprovePublishFailure(null);
            setInlineError(null);
            setToast({ type: 'success', message: RUN_REPLACED_TEXT });
            navigate(`/review/${replacing.id}`, {
              replace: true,
              state: { ...(location.state || {}), siblingIds: replaceSiblingId(location.state?.siblingIds, execution.id, replacing.id) },
            });
            return;
          }
        }
        setExecution(fresh || (err.code === 'publish_failed' ? { ...execution, status: 'failed' } : execution));
        if (docRes) {
          setDocumentStatus(docRes.status || null);
          setPublishStatus(docRes.publish_status || null);
        } else if (err.code === 'publish_failed') {
          setPublishStatus('failed');
        }
      }
    } finally {
      if (!approved) setApproving(false);
    }
  }, [execution, executionId, captureDocumentId, navigate, hasNext, siblingIds, currentIndex, location.state, refreshQueue, queueDocIds, queueIndex, navigateToQueueDoc, isAdmin]);

  // ── Retry a failed publish ──
  // A document with a workflow run is retried ON THAT RUN: its Publish node
  // runs again with the node's own destination, field selection and renames,
  // and the run completes. The manual publish route is for a document with no
  // run at all, confirmed at the moment of the retry.
  const retryPublish = useCallback(async (failure) => {
    const run = execution || (await findLatestRun(captureDocumentId));
    if (run) {
      if (run.status !== 'failed') {
        throw new Error(`This document's workflow run is ${run.status}; there is no failed publish to retry.`);
      }
      return captureAPI.workflows.retryFailedPublish(run.id);
    }
    if (!failure?.destination_id) throw new Error('No failed attempt is recorded for this document.');
    return captureAPI.publish.publishDocument(captureDocumentId, { destinationId: failure.destination_id });
  }, [execution, captureDocumentId]);

  // ── Discard flow ──
  const handleDiscard = useCallback(async () => {
    setRejecting(true);
    setInlineError(null);
    try {
      await capturePatch(
        `/api/capture/processing-monitor/documents/${captureDocumentId}/status`,
        { status: 'rejected', reason: discardReason || undefined },
        { headers }
      );

      setToast({ type: 'success', message: 'Document rejected.' });
      setShowDiscardConfirm(false);
      setDiscardReason('');
      // After rejection, advance to next document like approval does
      await refreshQueue();
      setTimeout(() => {
        const updatedQueue = queueDocIds.filter(id => id !== captureDocumentId);
        if (queueIndex >= 0 && updatedQueue.length > 0) {
          const nextIdx = Math.min(queueIndex, updatedQueue.length - 1);
          navigateToQueueDoc(updatedQueue[nextIdx]);
        } else {
          navigate('/queue');
        }
      }, 600);
    } catch (err) {
      setInlineError(err.message || 'Reject failed');
    } finally {
      setRejecting(false);
    }
  }, [captureDocumentId, discardReason, navigate, refreshQueue, queueDocIds, queueIndex, captureDocumentId, navigateToQueueDoc]);

  // ── Delete flow ──
  const handleDelete = useCallback(async () => {
    setDeleting(true);
    setInlineError(null);
    try {
      await captureDelete(`/api/capture/processing-monitor/documents/${captureDocumentId}`);

      setToast({ type: 'success', message: 'Document deleted.' });
      setShowDeleteConfirm(false);
      setTimeout(() => navigate('/queue'), 1200);
    } catch (err) {
      setInlineError(err.message || 'Delete failed');
    } finally {
      setDeleting(false);
    }
  }, [captureDocumentId, navigate]);

  // ── Re-extract (template first; optional vendor profile override) ──
  const loadVendorProfiles = useCallback(async () => {
    setLoadingProfiles(true);
    try {
      const res = await captureGet('/api/capture/vendor-profiles');
      setVendorProfiles(res.data?.profiles || []);
    } catch (err) {
      console.warn('[ReviewDetail] failed to load vendor profiles:', err?.message);
    } finally {
      setLoadingProfiles(false);
    }
  }, []);

  const handleReExtract = useCallback(async (confirmed = false) => {
    // Warn-before-reextract (Unit 3): re-extraction delete-all-rebuilds line
    // items, discarding manual corrections. If any exist and the operator has
    // not confirmed, show the inline warning and abort — nothing destructive
    // happens here. With no manual corrections, behavior is unchanged.
    if (!confirmed && hasManualLineItemCorrections(lineItems)) {
      setShowReExtractWarning(true);
      return;
    }
    setShowReExtractWarning(false);
    setReclassifying(true);
    setShowReExtractPanel(false);
    setInlineError(null);
    setReextractRefreshWarning(null);
    const docId = captureDocumentId || executionId;
    try {
      // 1. The run. Only this decides whether the re-extraction failed.
      let result;
      try {
        result = await reclassifyDocument(docId, 'reextract', selectedProfileId || undefined);
      } catch (err) {
        console.error('[ReviewDetail] re-extract: the request failed:', err);
        setInlineError(reextractFailureText(err?.response?.data));
        return;
      }
      if (!result?.ok) {
        console.error('[ReviewDetail] re-extract: the run did not succeed:', result);
        setInlineError(reextractFailureText(result));
        return;
      }
      if (result.restartedAt === 'action_split') {
        // Started, not finished: the batch is split again in the background.
        setToast({ type: 'success', message: reextractSuccessText(result, null) });
        return;
      }

      // 2. The page catching up. The run has succeeded whatever happens here,
      // so a failure is a warning about this page, never "Re-extraction failed."
      let step = 'reload fields';
      let fieldsResult = null;
      try {
        fieldsResult = await getExtractedFields(docId);
        step = 'apply fields';
        if (fieldsResult?.fields) {
          const next = mapExtractedFields(fieldsResult.fields);
          setFields(next.fields);
          setSourceMap(next.sourceMap);
          setFieldStatus(next.statusMap);
          setFieldEvidence(next.evidenceMap);
          // The rows are now the whole picture. Re-extract deletes every
          // capture_extracted_fields row before it runs (reextractTrigger), so
          // no correction made on this page survives it, and nothing from the
          // execution context is laid over what was just read.
          setOverlaidKeys({});
          setCorrectedFields({});
        }
        // Fix-along (Unit 3): re-extract rebuilds line items server-side; refetch
        // so the rebuilt rows show and the stale manual-flag tint clears.
        step = 'reload line items';
        await refetchLineItems({ throwOnError: true });
      } catch (err) {
        console.error(`[ReviewDetail] re-extract: the run finished, but "${step}" failed:`, err);
        setReextractRefreshWarning('Re-extraction finished, but this page could not reload the results. Refresh the page.');
      }
      setToast({ type: 'success', message: reextractSuccessText(result, fieldsResult?.fields?.length ?? null) });
      // The re-extract made calls of its own. Best-effort: a failed read keeps
      // the line it had.
      Promise.resolve()
        .then(() => captureAPI.getDocument(docId))
        .then((doc) => {
          if (doc?.ai_cost) setAiCost(doc.ai_cost);
          // The re-extract re-stamped the vendor snapshot from the new vendor_name.
          if (doc) applyVendorSnapshot(docId, doc);
        })
        .catch(() => {});

      // 3. The new run. The server cancelled this page's run and started another
      // (capture-app reextractTrigger), so open the new one: Approve then
      // resumes it, not the cancelled run, and a reload shows it too. Only a run
      // a reviewer still acts on: a run the workflow took straight through
      // (completed) has nothing to review, so the page stays where it is.
      const reviewable = result.status === 'paused' || result.status === 'failed';
      if (reviewable && result.executionId && result.executionId !== executionId) {
        navigate(`/review/${result.executionId}`, {
          replace: true,
          state: { ...(location.state || {}), siblingIds: replaceSiblingId(location.state?.siblingIds, executionId, result.executionId) },
        });
      }
    } finally {
      setReclassifying(false);
      setSelectedProfileId('');
    }
  }, [captureDocumentId, executionId, selectedProfileId, lineItems, refetchLineItems, navigate, location.state, applyVendorSnapshot]);

  // ── Document-type correction (v1a step 6) ──
  const docTypeOptions = useMemo(() => selectableDocumentTypes(docTypeRows || []), [docTypeRows]);
  const typeLabelByKey = useMemo(() => {
    const m = {};
    docTypeOptions.forEach((o) => { m[o.key] = o.label; });
    return m;
  }, [docTypeOptions]);
  // Registry label when loaded; formatDocType fallback covers the header row
  // before the (lazy) registry load and any current value not in the registry.
  const labelForType = useCallback(
    (key) => typeLabelByKey[key] || formatDocType(key),
    [typeLabelByKey]
  );

  const closeTypeCorrection = useCallback(() => {
    setShowTypePanel(false);
    setSelectedTypeKey('');
    setTypePreflight(null);
    setTypeError(null);
    typePreflightSeq.current += 1; // strand any in-flight preflight
    if (typePreflightAbortRef.current) {
      typePreflightAbortRef.current.abort();
      typePreflightAbortRef.current = null;
    }
  }, []);

  const openTypeCorrection = useCallback(async () => {
    setShowTypePanel(true);
    // Q8: current type pre-selected; 'unknown' tolerated as current only —
    // it renders as a disabled placeholder, never as a pickable target.
    setSelectedTypeKey(documentType && documentType !== 'unknown' ? documentType : '');
    setTypePreflight(null);
    setTypeError(null);
    if (docTypeRows == null) {
      setLoadingDocTypes(true);
      try {
        const res = await captureAPI.getDocumentTypes();
        setDocTypeRows(res.documentTypes || []);
      } catch (err) {
        console.error('[ReviewDetail] failed to load document types:', err);
        setTypeError('Failed to load document types. Close and retry.');
      } finally {
        setLoadingDocTypes(false);
      }
    }
  }, [documentType, docTypeRows]);

  // Q9: preflight fires on SELECTION CHANGE. Selecting the current type (or
  // the placeholder) clears it — no request, confirm stays disabled.
  const handleTypeSelectionChange = useCallback((key) => {
    setSelectedTypeKey(key);
    setTypeError(null);
    const seq = ++typePreflightSeq.current;
    if (typePreflightAbortRef.current) typePreflightAbortRef.current.abort();
    if (!key || key === documentType) {
      setTypePreflight(null);
      return;
    }
    const controller = new AbortController();
    typePreflightAbortRef.current = controller;
    setTypePreflight({ forType: key, status: 'loading' });
    captureAPI
      .typeCorrectionPreflight(captureDocumentId, key, { signal: controller.signal })
      .then((data) => {
        if (seq !== typePreflightSeq.current) return; // superseded
        setTypePreflight({ forType: key, status: 'ready', data });
      })
      .catch((err) => {
        if (seq !== typePreflightSeq.current) return; // superseded
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setTypePreflight({
          forType: key,
          status: 'error',
          message: correctionErrorMessage(err?.response?.status, err?.response?.data),
        });
      });
  }, [captureDocumentId, documentType]);

  const handleTypeCorrection = useCallback(async () => {
    if (!selectedTypeKey || selectedTypeKey === documentType) return;
    const warned = typePreflight?.status === 'ready' && shouldWarnLostAccess(typePreflight.data);
    setTypeSaving(true);
    setTypeError(null);
    try {
      // documentPatchSchema is .strict() — the body must be EXACTLY this key.
      // The response is the route's own fresh read carrying the co-stamped
      // department; use it instead of a follow-up GET, which would 404 for a
      // member who just corrected the document out of their departments.
      const fresh = await captureAPI.updateDocument(captureDocumentId, {
        document_type: selectedTypeKey,
      });
      const newType = fresh?.document_type || selectedTypeKey;
      setDocumentType(newType);
      if (fresh) {
        setDocumentStatus(fresh.status || null);
        // Additive (slice 1b): `fresh` is the route's own select('*') re-read,
        // so it already carries the classification columns the funnel just
        // rewrote. Syncing them here is what stops the evidence panel from
        // re-attributing the OLD type's phrases to the newly corrected one.
        // Nothing about the correction request or its semantics changes.
        setDocumentTypeSource(fresh.document_type_source ?? null);
        setDocumentTypeConfidence(fresh.document_type_confidence ?? null);
        setDocumentTypeEvidence(fresh.document_type_evidence ?? null);
      }
      setToast({
        type: 'success',
        message: warned
          ? `Document type corrected to ${labelForType(newType)}. The document moved to a department outside your access.`
          : `Document type corrected to ${labelForType(newType)}.`,
      });
      closeTypeCorrection();
    } catch (err) {
      // Toast, not inlineError — inlineError only renders when an execution
      // exists, and this control must work in document-direct mode too.
      const msg = correctionErrorMessage(err?.response?.status, err?.response?.data);
      setTypeError(msg);
      setToast({ type: 'error', message: msg });
    } finally {
      setTypeSaving(false);
    }
  }, [captureDocumentId, selectedTypeKey, documentType, typePreflight, labelForType, closeTypeCorrection]);

  // ── Author a proposal from the pending declaration ─────────────────────
  // Shared by the just-declared path and the "Continue setup" button, so the
  // two cannot drift. Spends one AI call; never throws.
  const requestSetupProposal = useCallback(async (declarationId) => {
    if (!declarationId || !captureDocumentId) return;
    setProposing(true);
    setProposeError(null);
    try {
      const res = await captureAPI.createSetupProposal(captureDocumentId, declarationId);
      setSetupProposal(res?.proposal ?? null);
      setSetupProposalId(res?.proposal_id ?? null);
      // proposal_id null means the row could not be persisted. The proposal is
      // readable but NOT applyable — apply must never fall back to trusting a
      // client-supplied body — so say so rather than showing a dead button.
      if (res?.proposal && !res?.proposal_id) {
        setProposeError(
          'The proposal could not be saved, so it cannot be applied yet. Try again in a moment.',
        );
      }
    } catch (err) {
      const data = err?.response?.data;
      setProposeError(
        data?.details || data?.error || err?.message || 'Could not draft the setup for this document.',
      );
    } finally {
      setProposing(false);
    }
  }, [captureDocumentId]);

  const handleApplySetup = useCallback(async (edits) => {
    if (!setupProposalId) return;
    setApplyingSetup(true);
    setApplyError(null);
    try {
      const res = await captureAPI.applySetupProposal(captureDocumentId, setupProposalId, edits);

      // 207: some ops landed and one did not. The proposal stays open and the
      // ops are idempotent, so re-applying COMPLETES it — which is why the
      // panel stays up rather than being cleared.
      if (res?.status === 207 || res?.ok === false) {
        setApplyError(
          res?.failure?.message
            ? `Partly created — ${res.failure.message} Press Create again to finish; nothing will be duplicated.`
            : 'Setup was only partly created. Press Create again to finish.',
        );
        return;
      }

      setSetupProposal(null);
      setSetupProposalId(null);
      setTypeDeclaration(null);
      setToast({
        type: 'success',
        message: `Created "${res?.document_type || 'the document type'}"${
          res?.warnings?.length ? ` — ${res.warnings[0]}` : '.'
        }`,
      });
      // Reload so the new document_type and its index fields actually appear;
      // the whole point of the flow is that the reviewer sees the result.
      loadData();
    } catch (err) {
      const data = err?.response?.data;
      const message = data?.details || data?.error || err?.message || 'Could not create the setup.';
      // phrases_required belongs on the keyword input — it is about that box.
      setApplyError(data?.error === 'phrases_required' ? { message, code: 'phrases_required' } : message);
    } finally {
      setApplyingSetup(false);
    }
  }, [captureDocumentId, setupProposalId, loadData]);

  // ── Turn one: the declaration ("it's something new") ───────────────────
  const handleDeclareType = useCallback(async ({ declared_label, field_wishes }) => {
    setDeclaring(true);
    setDeclareError(null);
    try {
      const res = await captureAPI.createTypeDeclaration(captureDocumentId, {
        declared_label,
        field_wishes,
      });
      if (res?.declaration) {
        setTypeDeclaration(res.declaration);
        // Straight on to the proposal — the 2026-09-02 direction change is that
        // declare and apply complete in one sitting. The declaration is already
        // saved, so a failure here loses nothing and is retryable.
        requestSetupProposal(res.declaration.id);
      }
      setToast({ type: 'success', message: `Recorded "${declared_label}". Drafting the setup…` });
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      // 409 declaration_pending is NOT a failure: two reviewers raced and the
      // row that won comes back under the same `declaration` key. Show theirs
      // rather than reporting an error for a question that is now answered.
      if (status === 409 && data?.declaration) {
        setTypeDeclaration(data.declaration);
        requestSetupProposal(data.declaration.id);
        setToast({
          type: 'success',
          message: 'A declaration for this document is already recorded.',
        });
        return;
      }
      setDeclareError(
        data?.details || data?.error || err?.message || 'Failed to record that type.'
      );
    } finally {
      setDeclaring(false);
    }
  }, [captureDocumentId, requestSetupProposal]);

  // Opens on its own only while nobody has decided; once the reviewer answers
  // "yes" or asks "Why?", their choice wins for the rest of the visit.
  const showEvidencePanel =
    evidencePanelOverride !== null
      ? evidencePanelOverride
      : shouldAutoOpen({
          documentType,
          confidence: documentTypeConfidence,
          source: documentTypeSource,
          hasPendingDeclaration: !!typeDeclaration,
        });

  const typeWarnLostAccess =
    typePreflight?.status === 'ready' && shouldWarnLostAccess(typePreflight.data);
  const typeConfirmDisabled = typeConfirmDisabledCheck({
    selection: selectedTypeKey,
    currentType: documentType,
    saving: typeSaving,
    preflight: typePreflight,
  });

  // ── Render: loading state ──
  if (redirectingToSplitReview) return null;

  if (loading) {
    return (
      <div className="rd-page">
        <div className="rd-loading">
          <div className="spinner" />
          <p>Loading review...</p>
        </div>
      </div>
    );
  }

  // ── Render: already reviewed ──
  if (error && error.startsWith('already_reviewed:')) {
    const status = error.split(':')[1];
    return (
      <div className="rd-page">
        <div className="rd-error-state">
          <div className="rd-error-icon">✅</div>
          <h3>Already Reviewed</h3>
          <p>This document has already been reviewed. Current status: <strong>{status}</strong></p>
          <Link to="/queue" className="rd-back-link">← Back to Queue</Link>
        </div>
      </div>
    );
  }

  // ── Render: error state ──
  if (error) {
    return (
      <div className="rd-page">
        <div className="rd-error-state">
          <div className="rd-error-icon">⚠️</div>
          <h3>Failed to Load</h3>
          <p>{error}</p>
          <Link to="/queue" className="rd-back-link">← Back to Queue</Link>
        </div>
      </div>
    );
  }

  // ── Render: main layout ──
  return (
    <div className="rd-page">
      {/* Toast */}
      {toast && <div className={`rd-toast ${toast.type}`}>{toast.message}</div>}

      {/* Queue progress bar */}
      {queueTotal > 0 && (
        <div className="rd-queue-progress-bar">
          <div
            className="rd-queue-progress-fill"
            style={{ width: `${Math.min(100, (approvedCount / totalQueueSize) * 100)}%` }}
          />
        </div>
      )}

      {/* Top bar */}
      <div className="rd-topbar">
        <Link to="/queue" className="rd-back-link">← Back to Queue</Link>
        <div className="rd-topbar-divider" />
        <span className="rd-topbar-title">{filename}</span>
        <div className="rd-topbar-spacer" />
        {queueTotal > 0 && (
          <div className="rd-topbar-nav">
            <button onClick={goToPrevDoc} disabled={!hasPrev} className="rd-nav-btn" title="Previous document ([ or ←)">‹ Prev</button>
            <span className="rd-nav-counter">
              {queuePosition > 0 ? `${queuePosition} of ${queueTotal}` : `${queueTotal} in queue`}
              {approvedCount > 0 && <span className="rd-nav-approved"> · {approvedCount} approved</span>}
            </span>
            <button onClick={goToNextDoc} disabled={!hasNext} className="rd-nav-btn" title="Next document (] or →)">Next ›</button>
          </div>
        )}
        {/* State-driven status pill (backlog #3a). Hidden on published
            documents — see PILL_BY_STATUS map for the supported states.
            Unknown statuses render nothing rather than a misleading
            default. The published case is covered by the green badge in
            the action bar (Sub-section B). */}
        {topbarPill && (
          <span className={`rd-topbar-status rd-topbar-status--${topbarPill.tone}`}>
            {topbarPill.label}
          </span>
        )}
        {canRetryFailedStep && (
          <button
            type="button"
            className="rd-nav-btn rd-retry-failed-step"
            onClick={handleRetryFailedStep}
            disabled={retryingFailedStep}
            title="Run this workflow again from the step it failed at"
          >
            {retryingFailedStep
              ? 'Retrying…'
              : failedStep.label ? `Retry failed step: ${failedStep.label}` : 'Retry failed step'}
          </button>
        )}
      </div>

      {/* Two-column body */}
      <div className="rd-body">
        {/* Left: PDF Viewer */}
        <div className="rd-viewer" style={{ flexBasis: splitPct + '%', maxWidth: splitPct + '%' }}>
          {viewerDoc ? (
            <SimplePDFViewer
              fileUrl={viewerDoc.downloadUrl}
              documentId={captureDocumentId}
              captureApiBase={CAPTURE_API_BASE}
              onSplitDelete={handleOpenDocumentStudio}
              onAppend={() => setAppendModalOpen(true)}
              onCorrectionDrawn={handleCorrectionDrawn}
              correctingFieldKey={correctingField}
              correctingFieldLabel={correctingField ? prettyLabel(correctingField, fieldLabelsByKey) : null}
              onTokenClicked={handleTokenClicked}
              onRotationChange={setViewerRotation}
            />
          ) : (
            <div className="rd-viewer-placeholder">
              <div className="rd-viewer-placeholder-icon">📄</div>
              <span>Unable to load document preview</span>
            </div>
          )}
        </div>

        {/* Right: Review Panel */}

        <div
          className="rd-resize-handle"
          onMouseDown={(e) => {
            e.preventDefault();
            const body = e.currentTarget.parentElement;
            const totalWidth = body.offsetWidth;
            const startX = e.clientX;
            const startPct = splitPct;
            document.body.style.userSelect = 'none';
            document.body.style.cursor = 'col-resize';
            const onMove = (moveEvent) => {
              const delta = moveEvent.clientX - startX;
              const newPct = Math.min(Math.max(startPct + (delta / totalWidth) * 100, 28), 72);
              setSplitPct(newPct);
              window.dispatchEvent(new Event('resize'));
            };
            const onUp = () => {
              document.body.style.userSelect = '';
              document.body.style.cursor = '';
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
        />

        <div className="rd-panel" style={{ flexBasis: (100 - splitPct) + '%', minWidth: '340px' }}>
          {/* Header */}
          <div className="rd-panel-header">
            <div className="rd-panel-filename">
              {getDocIcon(mimeType)} {filename}
            </div>
            {(execution || aiCostText(aiCost)) && (
              <div className="rd-panel-meta">
                {execution && (
                  <>
                    <span>
                      <span className="rd-panel-meta-icon">⚡</span> {workflowName || 'Workflow'}
                    </span>
                    {nodeLabel && <span>• {nodeLabel}</span>}
                    {runMetaText(execution) && <span>• {runMetaText(execution)}</span>}
                  </>
                )}
                {/* Shown without a run too: document-direct mode has a cost. */}
                {aiCostText(aiCost) && (
                  <span className="rd-ai-cost" title={aiCostTooltip(aiCost)} data-testid="rd-ai-cost">
                    {execution ? '• ' : ''}{aiCostText(aiCost)}
                  </span>
                )}
              </div>
            )}
            {/* Document type row (v1a step 6) — the type's first visible home.
                Rendered UNCONDITIONALLY (not gated on execution): document-direct
                mode needs the correction control too. Publish is a handoff, not
                a link (2026-08-12 ruling), so a published document is correctable
                in Capture like any other — Capture's copy simply stops
                propagating to Library at publish. */}
            <div className="rd-panel-meta rd-doctype-row">
              <span>
                <span className="rd-panel-meta-icon">🏷️</span> Document type:{' '}
                <strong>{labelForType(documentType)}</strong>
              </span>
              <span className="rd-doctype-actions">
                {/* Manual way in when the panel did not open on its own —
                    a confident classification is still worth being able to
                    interrogate. */}
                {!showEvidencePanel && (
                  <button
                    className="rd-doctype-edit-btn"
                    onClick={() => setEvidencePanelOverride(true)}
                    title="Why this document type?"
                  >
                    Why?
                  </button>
                )}
                {!showTypePanel && (
                  <button
                    className="rd-doctype-edit-btn"
                    onClick={openTypeCorrection}
                    title="Correct the document type"
                  >
                    ✏️ Correct
                  </button>
                )}
              </span>
            </div>

            {/* Guided setup v2, turn one (slice 1b). Opens itself when the
                classifier is not confident and nobody has answered yet.
                onDismiss is local only — "yes, that's right" writes nothing. */}
            {showEvidencePanel && (
              <ClassificationEvidencePanel
                documentType={documentType}
                confidence={documentTypeConfidence}
                source={documentTypeSource}
                evidence={documentTypeEvidence}
                declaration={typeDeclaration}
                canBuild={canBuild}
                onPickDifferentType={openTypeCorrection}
                onDeclare={handleDeclareType}
                busy={declaring}
                error={declareError}
                onDismiss={() => setEvidencePanelOverride(false)}
                // Offered only when a declaration is pending, canBuild allows
                // it, and there is no proposal on screen already — otherwise it
                // would spend a second AI call to replace the one being read.
                onContinueSetup={
                  typeDeclaration && canBuild && !setupProposal
                    ? () => requestSetupProposal(typeDeclaration.id)
                    : undefined
                }
                setupBusy={proposing}
              />
            )}

            {/* Guided setup v2, the review turn. The proposal is server-authored
                and server-stored; this panel edits a DELTA against it and applies
                by id. It is mounted here, under the evidence panel, so declare →
                propose → review → apply reads as one column of the same
                conversation. */}
            {proposeError && !setupProposal && (
              <div className="rd-instructions rd-setup-error">
                <div className="rd-instructions-label">Setup</div>
                <div className="rd-instructions-text">{proposeError}</div>
              </div>
            )}
            {setupProposal && setupProposalId && (
              <SetupProposalPanel
                proposal={setupProposal}
                busy={applyingSetup}
                error={applyError}
                onApply={handleApplySetup}
                onCancel={() => {
                  // Local only. The proposal row stays OPEN server-side, so
                  // reopening the document brings it back rather than paying for
                  // another AI call.
                  setSetupProposal(null);
                  setApplyError(null);
                }}
              />
            )}
          </div>

          {/* Document-type correction panel (v1a step 6) — composes the
              re-extract panel's select + actions shape with its conditional
              warning block. The warning wires to retainsAccess === false ONLY;
              departmentWillChange is context inside the message, never the
              trigger (117/117 live docs are derived — it would always fire). */}
          {showTypePanel && (
            <div className="rd-reextract-panel rd-doctype-panel">
              <div className="rd-reextract-title">Correct Document Type</div>
              <select
                className="rd-reextract-select"
                value={selectedTypeKey}
                onChange={(e) => handleTypeSelectionChange(e.target.value)}
                disabled={loadingDocTypes || typeSaving}
              >
                {loadingDocTypes && <option value="">Loading document types…</option>}
                {!loadingDocTypes && !docTypeOptions.some((o) => o.key === selectedTypeKey) && (
                  <option value="" disabled>
                    {documentType ? `${formatDocType(documentType)} (current)` : '— Select a document type —'}
                  </option>
                )}
                {!loadingDocTypes && docTypeOptions.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}{o.key === documentType ? ' (current)' : ''}
                  </option>
                ))}
              </select>

              {typePreflight?.status === 'loading' && (
                <div className="rd-doctype-preflight-status">Checking access…</div>
              )}
              {typePreflight?.status === 'error' && (
                <div className="rd-li-cell-error">{typePreflight.message}</div>
              )}
              {typeError && <div className="rd-li-cell-error">{typeError}</div>}

              {!typeWarnLostAccess && (
                <div className="rd-reextract-actions">
                  <button
                    className="rd-reject-cancel-btn"
                    onClick={closeTypeCorrection}
                    disabled={typeSaving}
                  >
                    Cancel
                  </button>
                  <button
                    className="rd-reextract-run-btn"
                    onClick={handleTypeCorrection}
                    disabled={typeConfirmDisabled}
                  >
                    {typeSaving ? 'Saving…' : 'Apply Correction'}
                  </button>
                </div>
              )}

              {/* D2 disclosure: shown pre-confirm, only when the preflight says
                  the acting user would actually lose access. The danger-styled
                  button here IS the confirm — the primary actions row hides so
                  there is exactly one way forward. */}
              {typeWarnLostAccess && (
                <div className="rd-reject-confirm rd-delete-confirm rd-reextract-warning">
                  <div className="rd-reject-confirm-title">⚠ You will lose access to this document</div>
                  <div className="rd-delete-confirm-desc">
                    {lostAccessMessage(typePreflight.data, labelForType(selectedTypeKey), formatDocType)}
                  </div>
                  <div className="rd-reject-actions">
                    <button
                      className="rd-reject-cancel-btn"
                      onClick={closeTypeCorrection}
                      disabled={typeSaving}
                    >
                      Cancel
                    </button>
                    <button
                      className="rd-reject-confirm-btn rd-delete-confirm-btn"
                      onClick={handleTypeCorrection}
                      disabled={typeConfirmDisabled}
                    >
                      {typeSaving ? 'Saving…' : 'Correct anyway'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Instructions */}
          {instructions && (
            <div className="rd-instructions">
              <div className="rd-instructions-label">Review Instructions</div>
              <div className="rd-instructions-text">{instructions}</div>
            </div>
          )}

          {/* Publish status: a failed publish, its reason, and a retry */}
          <PublishStatusNotice
            captureDocumentId={captureDocumentId}
            publishStatus={effectivePublishStatus}
            retry={retryPublish}
            retryWithoutAttempt={execution?.status === 'failed'}
            fallbackReason={approvePublishFailure?.reason || null}
            reference={approvePublishFailure?.errorId || null}
            onPublished={(destinationName) => {
              setPublishStatus('published');
              setDocumentStatus('published');
              setInlineError(null);
              setApprovePublishFailure(null);
              // The retried run has completed.
              if (execution) setExecution({ ...execution, status: 'completed', completed_at: new Date().toISOString() });
              setToast({
                type: 'success',
                message: destinationName ? `Published to ${destinationName}.` : 'Published.',
              });
            }}
          />

          {/* Inline error. A failed publish after Approve is the notice's to
              show; it appears here only if the notice is not on the page. */}
          {(inlineError || (approvePublishFailure && !publishFailed)) && (
            <div className="rd-inline-error" role="alert">
              {inlineError ||
                `Approved, but publishing failed: ${approvePublishFailure.reason}` +
                  (approvePublishFailure.errorId ? ` (error_id: ${approvePublishFailure.errorId})` : '')}
            </div>
          )}

          {reextractRefreshWarning && (
            <div className="rd-inline-warning" role="status">{reextractRefreshWarning}</div>
          )}

          {/* Discard confirmation */}
          {showDiscardConfirm && (
            <div className="rd-reject-confirm">
              <div className="rd-reject-confirm-title">Reject this document?</div>
              <input
                className="rd-reject-reason-input"
                type="text"
                placeholder="Optional reason..."
                value={discardReason}
                onChange={(e) => setDiscardReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleDiscard(); }}
                autoFocus
              />
              <div className="rd-reject-actions">
                <button
                  className="rd-reject-cancel-btn"
                  onClick={() => { setShowDiscardConfirm(false); setDiscardReason(''); }}
                  disabled={rejecting}
                >
                  Cancel
                </button>
                <button
                  className="rd-reject-confirm-btn"
                  onClick={handleDiscard}
                  disabled={rejecting}
                >
                  {rejecting ? 'Rejecting...' : 'Confirm Reject'}
                </button>
              </div>
            </div>
          )}

          {/* Delete confirmation */}
          {showDeleteConfirm && (
            <div className="rd-reject-confirm rd-delete-confirm">
              <div className="rd-reject-confirm-title">⚠ Permanently delete this document?</div>
              <div className="rd-delete-confirm-desc">This cannot be undone. The document and all extracted data will be removed.</div>
              <div className="rd-reject-actions">
                <button
                  className="rd-reject-cancel-btn"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  className="rd-reject-confirm-btn rd-delete-confirm-btn"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete Permanently'}
                </button>
              </div>
            </div>
          )}

          {/* Re-extract panel */}
          {showReExtractPanel && (
            <div className="rd-reextract-panel">
              <div className="rd-reextract-title">Re-extract this document</div>
              <div className="rd-reextract-desc">
                Runs extraction again from the start: a matching template first, then the
                document's vendor profile, then AI. Nothing runs until you press Start re-extraction.
              </div>
              <label className="rd-reextract-label" htmlFor="rd-reextract-profile">
                Vendor profile override (optional)
              </label>
              <select
                id="rd-reextract-profile"
                className="rd-reextract-select"
                value={selectedProfileId}
                onChange={(e) => setSelectedProfileId(e.target.value)}
              >
                <option value="">None: match automatically (recommended)</option>
                {loadingProfiles && <option disabled>Loading profiles...</option>}
                {vendorProfiles.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.vendor_name || p.name}{p.doc_type ? ` — ${p.doc_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}` : ''}
                  </option>
                ))}
              </select>
              <div className="rd-reextract-actions">
                <button
                  className="rd-reject-cancel-btn"
                  onClick={() => { setShowReExtractPanel(false); setShowReExtractWarning(false); setSelectedProfileId(''); }}
                >
                  Cancel
                </button>
                <button
                  className="rd-reextract-run-btn"
                  onClick={() => handleReExtract()}
                  disabled={reclassifying}
                >
                  {reclassifying ? 'Extracting...' : 'Start re-extraction'}
                </button>
              </div>

              {/* Warn-before-reextract (Unit 3): only shown when manual line-item
                  corrections exist and the operator clicked Run Extraction. */}
              {showReExtractWarning && (
                <div className="rd-reject-confirm rd-delete-confirm rd-reextract-warning">
                  <div className="rd-reject-confirm-title">⚠ Discard manual corrections?</div>
                  <div className="rd-delete-confirm-desc">
                    Re-extracting will discard your manual line-item corrections. Continue?
                  </div>
                  <div className="rd-reject-actions">
                    <button
                      className="rd-reject-cancel-btn"
                      onClick={() => setShowReExtractWarning(false)}
                      disabled={reclassifying}
                    >
                      Cancel
                    </button>
                    <button
                      className="rd-reject-confirm-btn rd-delete-confirm-btn"
                      onClick={() => handleReExtract(true)}
                      disabled={reclassifying}
                    >
                      {reclassifying ? 'Extracting...' : 'Re-extract anyway'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Action buttons */}
          <div className="rd-actions">
            <button
              className="rd-btn-reextract"
              onClick={() => {
                setShowReExtractPanel((v) => !v);
                setShowReExtractWarning(false);
                setShowDiscardConfirm(false);
                setShowDeleteConfirm(false);
                if (!showReExtractPanel) loadVendorProfiles();
              }}
              disabled={approving || rejecting || reclassifying || deleting}
              title="Choose how to re-run extraction. Nothing runs until you press Start re-extraction."
              aria-expanded={showReExtractPanel}
            >
              {reclassifying ? '⟳ Extracting...' : '⟳ Re-extract…'}
            </button>

            {!isPublished && !runEnded && (
              <button
                className="rd-btn-reject"
                onClick={() => {
                  setShowDiscardConfirm((v) => !v);
                  setShowReExtractPanel(false);
                  setShowDeleteConfirm(false);
                }}
                disabled={approving || rejecting || reclassifying || deleting}
                title="Reject this document from the review queue"
              >
                Reject
              </button>
            )}

            <button
              className="rd-btn-delete"
              onClick={() => {
                setShowDeleteConfirm((v) => !v);
                setShowReExtractPanel(false);
                setShowDiscardConfirm(false);
              }}
              disabled={approving || rejecting || reclassifying || deleting}
              title="Permanently delete this document"
            >
              🗑 Delete
            </button>

            <div className="rd-actions-spacer" />

            {isPublished ? (
              <span
                className="rd-published-badge"
                title="This document has already been published. Re-extract or correct fields to update; workflow approval is not available."
              >
                ✅ Published
              </span>
            ) : runEnded ? (
              <span
                className="rd-run-ended-badge"
                title="This document's workflow run has ended without publishing. Approval is not available; retry the publish above."
              >
                {publishFailed ? '⚠ Publish failed' : '⚠ Workflow run failed'}
              </span>
            ) : (
              <>
                <button
                  className="rd-btn-approve"
                  onClick={handleApprove}
                  disabled={approving || rejecting || deleting || flagging}
                >
                  {approving ? 'Approving...' : 'Approve & Continue'}
                </button>
                <button
                  className="rd-flag-btn"
                  onClick={() => setShowFlagReason(f => !f)}
                  disabled={approving || rejecting || deleting || flagging}
                  title="Approve with low confidence flag"
                >⚑</button>
              </>
            )}
          </div>
          {showFlagReason && (
            <div className="rd-flag-popover">
              <div className="rd-flag-popover-title">⚑ Approve with Low Confidence</div>
              <div className="rd-flag-popover-hint">Optionally describe why you are uncertain:</div>
              <textarea
                className="rd-flag-reason-input"
                value={flagReason}
                onChange={e => setFlagReason(e.target.value)}
                placeholder="e.g. Total field may be incorrect, OCR quality was poor..."
                rows={3}
                autoFocus
              />
              <div className="rd-flag-popover-actions">
                <button className="rd-flag-cancel" onClick={() => { setShowFlagReason(false); setFlagReason(''); }}>Cancel</button>
                <button className="rd-flag-confirm" onClick={handleFlagAndApprove} disabled={flagging}>
                  {flagging ? 'Saving...' : '⚑ Approve & Flag'}
                </button>
              </div>
            </div>
          )}

          {/* Vendor (slice 2b-3): renders nothing for a tenant without vendor records. */}
          <VendorBlock
            documentId={captureDocumentId}
            snapshot={vendorSnapshot}
            directoryInUse={vendorDirectoryInUse}
            printedVendorName={printedVendorName(correctedFields, fields)}
            readOnly={isPublished}
            onSnapshot={applyVendorSnapshot}
            onPublished={() => refreshAfterPublishedRefusal(captureDocumentId)}
          />

          {/* Fields */}
          <div className="rd-fields-section">
            <div className="rd-fields-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <span className="rd-fields-title-left">
                Index Fields ({sortedFieldKeys.length})
                {orderSaved && <span className="rd-order-saved" role="status">Order saved</span>}
                {fieldOrderPref && (
                  <button
                    type="button"
                    className="rd-order-reset"
                    onClick={handleResetOrder}
                    disabled={savingOrder}
                    title="Reset to default field order"
                  >
                    Reset order
                  </button>
                )}
              </span>
              {/* Builder-only CTA: build a reusable template from this AI extraction.
                  Shown only when the user can build, Claude produced fields, and no
                  template is already applied (don't offer to rebuild an existing one). */}
              {canBuild && hasClaudeFields && !appliedTemplateId && (
                <button
                  type="button"
                  onClick={handleCreateTemplate}
                  disabled={buildingTemplate}
                  title="Build a reusable template from this AI extraction"
                  style={{
                    fontSize: 12,
                    padding: '4px 10px',
                    borderRadius: 4,
                    border: '1px solid var(--accent, #2C6FB1)',
                    background: buildingTemplate ? '#e5e7eb' : 'var(--accent, #2C6FB1)',
                    color: buildingTemplate ? '#6b7280' : '#fff',
                    cursor: buildingTemplate ? 'default' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {buildingTemplate ? 'Creating Template…' : '+ Create Template'}
                </button>
              )}
            </div>
            {buildTemplateError && (
              <div
                role="alert"
                style={{ color: 'var(--danger, #dc2626)', fontSize: 12, margin: '4px 0' }}
              >
                Couldn’t create template: {buildTemplateError}
              </div>
            )}

            {sortedFieldKeys.length === 0 ? (
              <div className="rd-no-fields">No extracted fields available</div>
            ) : (
              sortedFieldKeys.map((key) => (
                <FieldRow
                  key={key}
                  fieldKey={key}
                  label={prettyLabel(key, fieldLabelsByKey)}
                  templateField={templateOnlyKeys.has(key)}
                  value={fields[key]}
                  fieldType={fieldDefs[key]?.field_type}
                  flagged={fieldStatus[key] === 'needs_review' && !(key in correctedFields)}
                  reason={reasonForField(key)}
                  isCorrected={key in correctedFields}
                  isCorrecting={key === correctingField}
                  onCorrect={handlePerFieldCorrect}
                  dragEnabled={!!documentType}
                  isDragging={draggedKey === key}
                  isDragOver={dragOverKey === key && draggedKey !== key}
                  onGripDragStart={(e) => handleFieldDragStart(e, key)}
                  onRowDragOver={(e) => handleFieldDragOver(e, key)}
                  onRowDrop={(e) => handleFieldDrop(e, key)}
                  onDragEnd={handleFieldDragEnd}
                />
              ))
            )}
          </div>

          {/* Document Links — Phase 5 (retiring; hidden behind SHOW_DOCUMENT_LINKS) */}
          {SHOW_DOCUMENT_LINKS && (
          <div className="rd-links-section">
            <div className="rd-links-header">
              <span className="rd-links-title">🔗 Linked Documents</span>
              <button className="rd-links-add-btn" onClick={() => setShowLinkForm(f => !f)}>+ Link</button>
            </div>
            {showLinkForm && (
              <div className="rd-link-form">
                <select className="rd-link-select" value={linkType} onChange={e => setLinkType(e.target.value)}>
                  <option value="matches_po">Matches PO</option>
                  <option value="matches_receipt">Matches Receipt</option>
                  <option value="duplicate_of">Duplicate Of</option>
                  <option value="continuation_of">Continuation Of</option>
                  <option value="related">Related</option>
                </select>
                <input
                  className="rd-link-ref-input"
                  type="text"
                  value={linkReference}
                  onChange={e => setLinkReference(e.target.value)}
                  placeholder="Reference (e.g. PO #MMG-PO-2024)..."
                  onKeyDown={e => { if (e.key === 'Enter') handleAddLink(); if (e.key === 'Escape') setShowLinkForm(false); }}
                  autoFocus
                />
                <input
                  className="rd-link-note-input"
                  type="text"
                  value={linkNote}
                  onChange={e => setLinkNote(e.target.value)}
                  placeholder="Note (optional)..."
                />
                <div className="rd-link-form-actions">
                  <button className="rd-link-cancel" onClick={() => setShowLinkForm(false)}>Cancel</button>
                  <button className="rd-link-save" onClick={handleAddLink} disabled={savingLink || !linkReference.trim()}>
                    {savingLink ? 'Saving...' : 'Add Link'}
                  </button>
                </div>
              </div>
            )}
            {links.length > 0 ? (
              <div className="rd-links-list">
                {links.map(link => (
                  <div key={link.id} className="rd-link-chip">
                    <span className="rd-link-chip-type">{link.link_type.replace(/_/g, ' ')}</span>
                    <span className="rd-link-chip-ref">{link.target_reference}</span>
                    {link.note && <span className="rd-link-chip-note">{link.note}</span>}
                    <button className="rd-link-chip-remove" onClick={() => handleRemoveLink(link.id)} title="Remove link">✕</button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="rd-links-empty">No linked documents</div>
            )}
          </div>
          )}

          {/* Line items */}
          {lineItems.length > 0 && (
            <div className="rd-line-items-section">
              <div className="rd-line-items-header">
                <button
                  className="rd-line-items-toggle"
                  onClick={() => setLineItemsExpanded(prev => !prev)}
                >
                  <span className="rd-line-items-toggle-label">
                    Line Items ({lineItems.length})
                  </span>
                  <span className={`rd-line-items-toggle-icon ${lineItemsExpanded ? 'expanded' : ''}`}>
                    ▼
                  </span>
                </button>
                <button
                  className="rd-line-items-popout-btn"
                  onClick={() => setLineItemsModalOpen(true)}
                  title="Open line items in full view"
                >
                  ⤢
                </button>
              </div>
              {lineItemsExpanded && (
                <div className="rd-line-items-table-wrap">
                  <table className="rd-line-items-table">
                    <thead>
                      <tr>
                        {visibleLineItemColumns(lineItems).map(k => (
                          <th key={k} className={isNumericLineItemColumn(k) ? 'rd-li-col-num' : undefined}>
                            {k.replace(/_/g, ' ')}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lineItems.map((item, idx) => (
                        <tr key={item.id || idx} className={item.source === 'manual' ? 'rd-li-row--manual' : undefined}>
                          {visibleLineItemColumns(lineItems).map(k => {
                            const numeric = isNumericLineItemColumn(k);
                            const editable = isEditableLineItemColumn(k);
                            const isEditing = editingCell && editingCell.lineItemId === item.id && editingCell.columnKey === k;
                            const val = item[k] ?? '';
                            const display = typeof val === 'object' ? JSON.stringify(val) : String(val);
                            // Read display only — currency columns render as
                            // $1,234.56. The edit input below seeds from the RAW
                            // value (lineItemInputSeed), never this formatted text.
                            const readDisplay = isCurrencyLineItemColumn(k)
                              ? formatFieldValue(display, 'currency')
                              : display;
                            const cellClass = [
                              numeric ? 'rd-li-col-num' : '',
                              editable ? 'rd-li-cell--editable' : '',
                            ].filter(Boolean).join(' ') || undefined;
                            if (isEditing) {
                              return (
                                <td key={k} className={cellClass}>
                                  <input
                                    className="rd-li-cell-input"
                                    type={numeric ? 'number' : 'text'}
                                    value={editingValue}
                                    autoFocus
                                    disabled={savingCell}
                                    onFocus={e => e.target.select()}
                                    onChange={e => setEditingValue(e.target.value)}
                                    onKeyDown={handleCellKeyDown}
                                    onBlur={commitCellEdit}
                                  />
                                  {editingError && <div className="rd-li-cell-error">{editingError}</div>}
                                </td>
                              );
                            }
                            return (
                              <td
                                key={k}
                                className={cellClass}
                                title={editable ? 'Double-click to edit' : display}
                                onDoubleClick={editable ? () => beginCellEdit(item, k) : undefined}
                              >
                                {readDisplay}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Extraction Notes */}
          {notesProfileId && (
            <div className="rd-extraction-notes">
              <button
                className="rd-notes-toggle"
                onClick={() => setNotesExpanded(prev => !prev)}
              >
                <span>✏️ Extraction Notes{notesVendorName ? ` — ${notesVendorName}${documentType ? ` (${formatDocType(documentType)})` : ''}` : ''}</span>
                <span className={`rd-notes-arrow ${notesExpanded ? 'expanded' : ''}`}>▼</span>
              </button>
              {notesExpanded && (
                <div className="rd-notes-body">
                  <p className="rd-notes-hint">
                    Describe what Claude got wrong and how to fix it. These notes will be sent to Claude on every future extraction for this vendor.
                  </p>
                  <textarea
                    className="rd-notes-textarea"
                    value={extractionNotes}
                    onChange={e => setExtractionNotes(e.target.value)}
                    placeholder={`Example: "The Item # column contains SKU codes like S-7112. The Description column contains the full product name. Extract both separately."`}
                    rows={5}
                  />
                  <div className="rd-notes-actions">
                    <button
                      className="rd-notes-save-btn"
                      onClick={handleSaveNotes}
                      disabled={notesSaving}
                    >
                      {notesSaving ? 'Saving...' : notesSaved ? '✓ Saved' : 'Save Notes'}
                    </button>
                    {extractionNotes && (
                      <button
                        className="rd-notes-clear-btn"
                        onClick={() => setExtractionNotes('')}
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Workflow History */}
          {execution?.id && workflowSteps.length > 0 && (
            <div className="rd-workflow-history">
              <button
                className="rd-history-toggle"
                onClick={() => setWorkflowHistoryExpanded(prev => !prev)}
              >
                <span>📋 Workflow History ({workflowSteps.length} steps)</span>
                <span className={`rd-history-arrow ${workflowHistoryExpanded ? 'expanded' : ''}`}>▼</span>
              </button>
              {workflowHistoryExpanded && (
                <div className="rd-history-body">
                  <div className="rd-history-timeline">
                    {workflowSteps.map((step, idx) => (
                      <WorkflowStepItem
                        key={step.id}
                        step={step}
                        isLast={idx === workflowSteps.length - 1}
                        completedSteps={workflowSteps.filter(s => s.status === 'completed')}
                        onRetry={handleRetryNode}
                        onRetryFailedStep={handleRetryFailedStep}
                        canRetryFailedStep={canRetryFailedStep && failedStep.nodeId === step.node_id}
                        retryingFailedStep={retryingFailedStep}
                        onRewind={handleRewindExecution}
                        retryingNodeId={retryingNodeId}
                        rewindingToNodeId={rewindingToNodeId}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Append Document Modal */}
      {appendModalOpen && (
        <div className="rd-line-items-modal-overlay">
          <div className="rd-line-items-modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="rd-line-items-modal-header">
              <div className="rd-line-items-modal-title">Append to — {filename}</div>
              <button className="rd-line-items-modal-close" onClick={() => { setAppendModalOpen(false); setAppendFile(null); }}>✕</button>
            </div>
            <div className="rd-line-items-modal-body" style={{ padding: '20px' }}>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                Browse for a PDF to append to the end of <strong>{filename}</strong>. The pages will be added and this document will be re-processed.
              </p>
              <label style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px dashed #d1d5db',
                borderRadius: 8,
                padding: '32px 20px',
                cursor: 'pointer',
                background: appendFile ? '#f0fdf4' : '#f9fafb',
                borderColor: appendFile ? '#86efac' : '#d1d5db',
                transition: 'all 0.15s',
              }}>
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: 'none' }}
                  onChange={e => setAppendFile(e.target.files?.[0] || null)}
                />
                <span style={{ fontSize: 28, marginBottom: 8 }}>{appendFile ? '✅' : '📄'}</span>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#374151' }}>
                  {appendFile ? appendFile.name : 'Click to browse for PDF'}
                </span>
                {appendFile && (
                  <span style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                    {(appendFile.size / 1024).toFixed(0)} KB — click to change
                  </span>
                )}
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                <button
                  onClick={() => { setAppendModalOpen(false); setAppendFile(null); }}
                  style={{ padding: '7px 16px', border: '1px solid #d1d5db', borderRadius: 5, background: '#fff', cursor: 'pointer', fontSize: 13 }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleAppend}
                  disabled={!appendFile || appendWorking}
                  style={{ padding: '7px 16px', border: 'none', borderRadius: 5, background: appendFile ? 'var(--stm-accent-info)' : 'var(--stm-border-strong)', color: '#fff', cursor: appendFile ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 500 }}
                >
                  {appendWorking ? 'Appending...' : 'Append PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Line Items Full-Screen Modal */}
      {lineItemsModalOpen && lineItems.length > 0 && (
        <div className="rd-line-items-modal-overlay">
          <div
            className="rd-line-items-modal"
            ref={modalDragRef}
            style={modalPos.x !== null ? { position: 'fixed', left: modalPos.x, top: modalPos.y, transform: 'none' } : {}}
            onClick={e => e.stopPropagation()}
          >
            <div className="rd-line-items-modal-header" onMouseDown={handleModalMouseDown} style={{ cursor: 'grab' }}>
              <div className="rd-line-items-modal-title">
                Line Items ({lineItems.length}) — {filename}
              </div>
              <button
                className="rd-line-items-modal-close"
                onClick={() => setLineItemsModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="rd-line-items-modal-body">
              {/* Read-only full view (Unit 2: editing lives in the main table only). */}
              <table className="rd-line-items-modal-table">
                <thead>
                  <tr>
                    {visibleLineItemColumns(lineItems).map(k => (
                      <th key={k} className={isNumericLineItemColumn(k) ? 'rd-li-col-num' : undefined}>
                        {k.replace(/_/g, ' ')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lineItems.map((item, idx) => (
                    <tr key={item.id || idx} className={item.source === 'manual' ? 'rd-li-row--manual' : undefined}>
                      {visibleLineItemColumns(lineItems).map(k => {
                        const val = item[k] ?? '';
                        const display = typeof val === 'object' ? JSON.stringify(val) : String(val);
                        // Read-only full view: currency columns render formatted.
                        const readDisplay = isCurrencyLineItemColumn(k)
                          ? formatFieldValue(display, 'currency')
                          : display;
                        return (
                          <td key={k} className={isNumericLineItemColumn(k) ? 'rd-li-col-num' : undefined}>
                            {readDisplay}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Document Studio Overlay */}
      {correctionModal && (
        <FieldCorrectionModal
          isOpen={true}
          fieldKeys={sortedFieldKeys}
          fields={fields}
          correctedValue={correctionModal.correctedValue || ''}
          initialField={correctionModal.initialField || correctingField || ''}
          lockField={!!correctionModal.locked}
          anchorText={correctionModal.anchorText ?? null}
          valuePosition={correctionModal.valuePosition ?? null}
          rotation={viewerRotation}
          onConfirm={handleCorrectionConfirm}
          onCancel={handleCorrectionCancel}
        />
      )}

      {showDocumentStudio && viewerDoc && (
        <DocumentStudio
          documentId={captureDocumentId}
          documentUrl={viewerDoc.downloadUrl}
          documentName={filename}
          aiSplitSuggestions={aiSplitSuggestions}
          onApply={handleDocumentStudioApply}
          onCancel={handleDocumentStudioCancel}
        />
      )}
    </div>
  );
};

// ── Workflow Step Item Component ──

function WorkflowStepItem({ step, isLast, completedSteps, onRetry, onRetryFailedStep, canRetryFailedStep, retryingFailedStep, onRewind, retryingNodeId, rewindingToNodeId }) {
  const [showRewindMenu, setShowRewindMenu] = useState(false);
  
  const statusConfig = {
    completed: { color: '#10b981', icon: '✓', label: 'Completed' },
    failed: { color: '#ef4444', icon: '✕', label: 'Failed' },
    paused: { color: '#f59e0b', icon: '⏸', label: 'Paused' },
    skipped: { color: '#9ca3af', icon: '⤼', label: 'Skipped' },
    started: { color: 'var(--stm-accent-info)', icon: '⟳', label: 'Running' },
  };

  const config = statusConfig[step.status] || statusConfig.started;
  
  const formatDuration = (ms) => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const date = new Date(ts);
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getSummaryText = (step) => {
    if (!step.output_summary) return null;
    const summary = step.output_summary;
    
    switch (step.node_type) {
      case 'action_await_ocr':
      // Not a deploy-window alias: step logs record the node type as the
      // definition spelled it, so executions from before the rename keep
      // 'action_ocr' permanently. This case outlives the migration.
      case 'action_ocr':
        return `Text acquired: ${summary.engine || 'unknown'} • ${summary.pageCount || 0} pages`;
      case 'action_extract':
        return `Extracted ${summary.fieldCount || 0} fields • ${summary.pathway || 'unknown'} pathway${summary.costUsd ? ` • $${summary.costUsd.toFixed(4)}` : ''}`;
      case 'action_human_review':
        return `Paused for review • ${summary.fieldCount || 0} fields`;
      case 'action_split':
        return `Split into ${summary.splitCount || 0} documents`;
      case 'action_route':
        return `Routed: ${summary.decision || 'unknown'}${summary.confidence ? ` (${(summary.confidence * 100).toFixed(0)}%)` : ''}`;
      case 'action_validate':
        return summary.isValid ? 'Validation passed' : `Validation failed: ${summary.validationErrors?.length || 0} errors`;
      case 'action_publish':
        return `Published to ${summary.destination || 'unknown'}`;
      default:
        return null;
    }
  };

  const summaryText = getSummaryText(step);
  const isRetrying = retryingNodeId === step.node_id;
  const isRewinding = rewindingToNodeId === step.node_id;

  return (
    <div className="rd-step-item">
      <div className="rd-step-line-container">
        <div className="rd-step-dot" style={{ backgroundColor: config.color }} />
        {!isLast && <div className="rd-step-line" />}
      </div>
      
      <div className="rd-step-content">
        <div className="rd-step-header">
          <div className="rd-step-label">{step.node_label}</div>
          <div className="rd-step-status" style={{ color: config.color }}>
            <span className="rd-step-status-icon">{config.icon}</span>
            {config.label}
          </div>
        </div>
        
        <div className="rd-step-meta">
          <span>{formatTimestamp(step.started_at)}</span>
          {step.duration_ms && <span>• {formatDuration(step.duration_ms)}</span>}
          {step.initiated_by && step.initiated_by !== 'system' && (
            <span>• by {step.initiated_by}</span>
          )}
        </div>

        {summaryText && (
          <div className="rd-step-summary">{summaryText}</div>
        )}

        {step.error_message && (
          <div className="rd-step-error">
            <strong>Error:</strong> {step.error_message}
          </div>
        )}

        <div className="rd-step-actions">
          {/* A failed Publish step is retried by anyone who can review. Any
              other failed step is retried by an admin, and only the step the
              run actually stopped at: the server re-runs that one, whichever
              row was pressed. */}
          {step.status === 'failed' && step.node_type === 'output_publish' && (
            <button
              className="rd-step-action-btn rd-step-retry-btn"
              onClick={() => onRetry(step.node_id)}
              disabled={isRetrying}
            >
              {isRetrying ? 'Retrying...' : '↻ Retry this step'}
            </button>
          )}
          {step.status === 'failed' && step.node_type !== 'output_publish' && canRetryFailedStep && (
            <button
              className="rd-step-action-btn rd-step-retry-btn"
              onClick={onRetryFailedStep}
              disabled={retryingFailedStep}
            >
              {retryingFailedStep ? 'Retrying...' : '↻ Retry this step'}
            </button>
          )}
          
          {step.status === 'paused' && completedSteps.length > 0 && (
            <div className="rd-step-rewind-container">
              <button
                className="rd-step-action-btn rd-step-rewind-btn"
                onClick={() => setShowRewindMenu(!showRewindMenu)}
              >
                ⤺ Send back to...
              </button>
              {showRewindMenu && (
                <div className="rd-step-rewind-menu">
                  {completedSteps.map(completedStep => (
                    <button
                      key={completedStep.node_id}
                      className="rd-step-rewind-option"
                      onClick={() => {
                        onRewind(completedStep.node_id);
                        setShowRewindMenu(false);
                      }}
                      disabled={isRewinding}
                    >
                      {isRewinding && rewindingToNodeId === completedStep.node_id ? 'Rewinding...' : completedStep.node_label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ReviewDetailPage;
